import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CopyField from '@app/components/CopyField';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import Drawer from '@app/components/Drawer';
import KpiCard from '@app/components/KpiCard';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import Tabs from '@app/components/Tabs';
import { ActivityIcon, CopyIcon, DownloadIcon, RefreshIcon, ShieldIcon } from '@app/components/icons';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useActiveOrgDisplayName, useActiveOrgId } from '@app/hooks/useActiveOrg';
import type { CursorListResponse, SiteSummary } from '@app/types/org-console';
import { buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { formatDateTime, formatDuration, formatNumber, formatRelativeTime } from '@app/utils/format';
import { useConfig } from '@config/provider';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';

const RUNS_LIMIT = 20;
const JOBS_LIMIT = 50;
const DEFAULT_RULE_PACKS = ['foundation', 'management', 'topology', 'wan', 'control_plane', 'standardization'] as const;
const DEFAULT_RULE_PACK_ID_SET = new Set<string>(DEFAULT_RULE_PACKS);
const TABLE_PAGE_SIZE_OPTIONS: number[] = [10, 25, 50];
const DEFAULT_TABLE_PAGE_SIZE = 10;

type PostureScopeType = 'org' | 'site';
type PostureTriggerType = 'periodic' | 'event_driven' | 'on_demand' | string;
type PostureRunStatus = 'pending' | 'running' | 'cancel_requested' | 'cancelled' | 'completed' | 'completed_with_gaps' | 'failed' | string;
type PostureCoverageStatus = 'complete' | 'degraded' | 'unknown' | string;
type PostureSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical' | string;
type PostureJobStatus = 'active' | 'paused' | string;
type PostureScheduleMode = 'manual' | 'recurring';
type PosturePageTab = 'summary' | 'findings' | 'setup';

interface PostureSummary {
  orgId: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  overallScore: number;
  domainScores?: Record<string, number>;
  findingCounts?: Record<string, number>;
  coverageStatus: PostureCoverageStatus;
  strengthItems?: PostureSummaryItem[];
  priorityActionItems?: PostureSummaryItem[];
  coverageNotes?: PostureCoverageNote[];
  strengths?: string[];
  priorityActions?: string[];
  metadata?: Record<string, string>;
}

interface PostureSummaryRuleLink {
  linkType?: string;
  ruleId: string;
  status?: string;
  scopeType?: PostureScopeType | string;
  scopeId?: string;
  siteId?: string;
  subjectType?: string;
  subjectId?: string;
  subjectParentType?: string;
  subjectParentId?: string;
  domain?: string;
  metadata?: Record<string, string>;
}

interface PostureSummaryItem {
  label: string;
  kind?: string;
  source?: string;
  ruleLinks?: PostureSummaryRuleLink[];
}

interface PostureFindingDeltaSummary {
  status: string;
  previousRunId?: string;
  previousFinishedAt?: string;
  newFindings: number;
  persistingFindings: number;
  clearedFindings: number;
}

interface PostureFinding {
  ruleId: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  severity: PostureSeverity;
  domain: string;
  title: string;
  detail: string;
  action?: string;
  bucketId?: string;
  bucketLabel?: string;
  scopeLabel?: string;
  subjectLabel?: string;
  subjectParentLabel?: string;
  siteLabel?: string;
  ruleLabel?: string;
  plainTitle?: string;
  plainDetail?: string;
  plainAction?: string;
  remediation?: PostureRemediation;
  evidence?: PostureEvidence[];
  confidence?: string;
}

interface PostureRemediation {
  type?: string;
  intent?: string;
  summary?: string;
  platform?: string;
  commands?: string[];
  caveats?: string[];
  requiresReview?: boolean;
  proposal?: PostureRemediationProposal;
  review?: PostureRemediationReview;
  audit?: PostureRemediationAuditEntry[];
  defaultVariant?: string;
  variants?: PostureRemediationVariant[];
}

interface PostureRemediationVariant {
  kind?: string;
  source?: string;
  summary?: string;
  commands?: string[];
  caveats?: string[];
  proposal?: PostureRemediationProposal;
}

interface PostureRemediationProposal {
  format?: string;
  target?: string;
  strategy?: string;
  changes?: PostureRemediationProposalChange[];
  placeholders?: PostureRemediationPlaceholder[];
}

interface PostureRemediationProposalChange {
  path?: string;
  diff?: PostureRemediationProposalDiffEntry[];
}

interface PostureRemediationProposalDiffEntry {
  op?: string;
  line?: string;
  value?: string;
}

interface PostureRemediationPlaceholder {
  token?: string;
  description?: string;
}

interface PostureRemediationReview {
  state?: string;
  requiresApproval?: boolean;
}

interface PostureRemediationAuditEntry {
  note?: string;
  generatedAt?: string;
  generatedBy?: string;
}

interface PostureDeviceSummary {
  id?: string;
  displayName?: string;
  hostname?: string;
  mgmtAddress?: string;
}

interface PostureEvidence {
  category?: string;
  kind: string;
  locator: string;
  note?: string;
  values?: string[];
  metadata?: Record<string, string>;
  references?: PostureEvidenceReference[];
}

interface PostureEvidenceReference {
  scopeType?: string;
  scopeId?: string;
  label?: string;
  detail?: string;
}

interface PostureFindingRecord {
  runId: string;
  orgId: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  triggerType: PostureTriggerType;
  status: PostureRunStatus;
  coverageStatus: PostureCoverageStatus;
  requestedAt?: string;
  finishedAt?: string;
  schemaVersion?: string;
  finding: PostureFinding;
}

interface PostureFindingGroupRecord {
  key: string;
  ruleId: string;
  title: string;
  detail: string;
  domain: string;
  severity: string;
  requestedAt?: string;
  recommendedAction: string;
  evidenceCount: number;
  memberCount: number;
  scopeCount: number;
  deviceCount: number;
  siteCount: number;
  records: PostureFindingRecord[];
}

interface PostureFindingList {
  groups?: PostureFindingGroupRecord[];
  findingTotal?: number;
  items: PostureFindingRecord[];
  total: number;
  limit: number;
  offset: number;
}

interface PostureFindingRollup {
  runId: string;
  orgId: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  triggerType: PostureTriggerType;
  status: PostureRunStatus;
  coverageStatus: PostureCoverageStatus;
  requestedAt?: string;
  finishedAt?: string;
  schemaVersion?: string;
  totalFindings: number;
  bySeverity?: Record<string, number>;
  byDomain?: Record<string, number>;
  byDomainSeverity?: Record<string, Record<string, number>>;
}

interface PostureReport {
  format?: string;
  content?: string;
  schemaVersion?: string;
}

interface PostureRunError {
  code?: string;
  message?: string;
}

interface PostureRunView {
  runId: string;
  orgId: string;
  jobId?: string;
  parentRunId?: string;
  name?: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  triggerType: PostureTriggerType;
  status: PostureRunStatus;
  coverageStatus: PostureCoverageStatus;
  enabledRulePacks?: string[];
  configFingerprint?: string;
  requestedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  summary: PostureSummary;
  findings?: PostureFinding[];
  report?: PostureReport | null;
  error?: PostureRunError | null;
  progressSummary?: Record<string, any>;
}

interface PostureRunList {
  items: PostureRunView[];
  total: number;
  limit: number;
  offset: number;
}

interface PostureOverview {
  runId: string;
  orgId: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  triggerType: PostureTriggerType;
  status: PostureRunStatus;
  coverageStatus: PostureCoverageStatus;
  requestedAt?: string;
  finishedAt?: string;
  schemaVersion?: string;
  delta?: PostureFindingDeltaSummary;
  summary: PostureSummary;
  findings: PostureFindingRollup;
  totalFindingsInOrg?: number;
  strengthItems?: PostureSummaryItem[];
  priorityActionItems?: PostureSummaryItem[];
  coverageNotes?: PostureCoverageNote[];
  strengths?: string[];
  priorityActions?: string[];
  reportAvailable: boolean;
}

interface PostureDeltaView {
  runId: string;
  orgId: string;
  scopeType: PostureScopeType | string;
  scopeId: string;
  triggerType: PostureTriggerType;
  runStatus: PostureRunStatus;
  coverageStatus: PostureCoverageStatus;
  requestedAt?: string;
  finishedAt?: string;
  schemaVersion?: string;
  deltaStatus: string;
  previousRunId?: string;
  previousFinishedAt?: string;
  newFindingCount: number;
  persistingFindingCount: number;
  clearedFindingCount: number;
  newFindings?: PostureFinding[];
  persistingFindings?: PostureFinding[];
  clearedFindings?: PostureFinding[];
}

interface PostureJobSchedule {
  mode?: PostureScheduleMode | string;
  cron?: string;
  triggerScope?: string;
}

interface PostureJobView {
  jobId: string;
  orgId: string;
  name: string;
  scopeType: PostureScopeType | string;
  scopeSelector?: Record<string, any>;
  schedule?: PostureJobSchedule;
  enabledRulePacks?: string[];
  disabledRuleIds?: string[];
  reportOptions?: Record<string, any>;
  status: PostureJobStatus;
  scheduledTaskId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PostureJobList {
  items: PostureJobView[];
  total: number;
  limit: number;
  offset: number;
}

interface PostureJobTriggerResult {
  job: PostureJobView;
  orgRun?: PostureRunView;
  runs?: PostureRunView[];
  failedSites?: Array<{
    siteId: string;
    error: string;
    started?: boolean;
    durable?: boolean;
  }>;
  requested: number;
  started: number;
  skippedActive: number;
}

interface PostureRulePackDescriptor {
  id: string;
  kind?: 'coarse' | 'detailed' | string;
  label?: string;
  defaultEnabled?: boolean;
  aliases?: string[];
}

interface PostureRulePackCatalog {
  items: PostureRulePackDescriptor[];
  total: number;
}

interface ManualRunFormState {
  enabledRulePacks: string[];
}

interface JobFormState {
  name: string;
  scopeType: PostureScopeType;
  siteId: string;
  scheduleMode: PostureScheduleMode;
  cron: string;
  status: 'active' | 'paused';
  enabledRulePacks: string[];
}

interface PostureSuccessResponseOptions {
  requireJson?: boolean;
}

interface PostureSuccessResponseWithoutJson extends PostureSuccessResponseOptions {
  requireJson: false;
}

interface PostureRouteState {
  scopeType: PostureScopeType;
  siteId: string;
  selectedRunId: string;
  findingsIncludeSiteItems: boolean;
  findingsSearch: string;
  findingsHotspotsOnly: boolean;
  findingsHasEvidenceOnly: boolean;
  findingsHasRemediationOnly: boolean;
  findingsHasRecommendationOnly: boolean;
  findingsSeverityFilter: string;
  findingsDomainFilter: string;
  findingsSort: 'severity_desc' | 'requested_desc' | 'domain_asc';
  runsStatusFilter: string;
  runsTriggerFilter: string;
  runsSort: 'requested_desc' | 'score_desc' | 'status_asc';
  jobsStatusFilter: string;
  jobsScopeFilter: string;
  jobsSort: 'updated_desc' | 'name_asc' | 'scope_asc';
}

type PostureFindingsViewMode = 'grouped' | 'individual';

interface PostureReportStats {
  lengthLabel: string;
  headingCount: number;
}

interface PostureCoverageNote {
  title: string;
  source?: string;
  detail?: string;
  ruleLinks?: PostureSummaryRuleLink[];
  items?: PostureCoverageNoteItem[];
}

interface PostureCoverageNoteItem {
  key: string;
  label: string;
  href?: string;
  detail?: string;
}

interface PostureInlineActionFeedback {
  message: string;
  tone: 'success' | 'warning';
  at: string;
}

const PosturePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ runId?: string; findingKey?: string }>();
  const routeState = readPostureRouteState();
  const orgId = useActiveOrgId();
  const orgDisplayName = useActiveOrgDisplayName();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgCollection, fetchOrgResource, fetchWithAuthRetry } = useAuthFetch();
  const routeRunId = params.runId?.trim() ?? '';
  const routeFindingKey = params.findingKey?.trim() ?? '';
  const isFindingRoute = routeRunId.length > 0 && routeFindingKey.length > 0;

  const [scopeType, setScopeType] = useState<PostureScopeType>(routeState.scopeType);
  const [activeTab, setActiveTab] = useState<PosturePageTab>('summary');
  const [selectedSiteId, setSelectedSiteId] = useState(routeState.siteId);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(routeState.selectedRunId || null);
  const [selectedFinding, setSelectedFinding] = useState<PostureFindingRecord | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const findingsSectionRef = useRef<HTMLDivElement | null>(null);
  const [runFeedbackById, setRunFeedbackById] = useState<Record<string, PostureInlineActionFeedback>>({});
  const [jobFeedbackById, setJobFeedbackById] = useState<Record<string, PostureInlineActionFeedback>>({});
  const [deviceSummaryById, setDeviceSummaryById] = useState<Record<string, PostureDeviceSummary | null>>({});
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [runDrawerOpen, setRunDrawerOpen] = useState(false);
  const [runSubmitting, setRunSubmitting] = useState(false);
  const [runSubmitError, setRunSubmitError] = useState<string | null>(null);
  const [runSubmitSuccess, setRunSubmitSuccess] = useState<string | null>(null);
  const [runCancelBusyId, setRunCancelBusyId] = useState<string | null>(null);
  const [runCancelError, setRunCancelError] = useState<string | null>(null);
  const [reportActionStatus, setReportActionStatus] = useState<string | null>(null);
  const [runForm, setRunForm] = useState<ManualRunFormState>({
    enabledRulePacks: []
  });
  const [runRulePacksDirty, setRunRulePacksDirty] = useState(false);

  const [jobDrawerOpen, setJobDrawerOpen] = useState(false);
  const [jobSaving, setJobSaving] = useState(false);
  const [jobSaveError, setJobSaveError] = useState<string | null>(null);
  const [jobSaveSuccess, setJobSaveSuccess] = useState<string | null>(null);
  const [jobSaveTone, setJobSaveTone] = useState<'success' | 'warning' | null>(null);
  const [jobTriggerBusyId, setJobTriggerBusyId] = useState<string | null>(null);
  const [jobToggleBusyId, setJobToggleBusyId] = useState<string | null>(null);
  const [jobDeleteBusyId, setJobDeleteBusyId] = useState<string | null>(null);
  const [pendingDeleteJob, setPendingDeleteJob] = useState<PostureJobView | null>(null);
  const [locallyDeletedJobIds, setLocallyDeletedJobIds] = useState<Set<string>>(() => new Set());
  const [jobDeleteToast, setJobDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [jobActionError, setJobActionError] = useState<string | null>(null);
  const [jobsState, setJobsState] = useState<PostureJobList | null>(null);
  const [overviewCoverageExpanded, setOverviewCoverageExpanded] = useState(false);
  const [findingsView, setFindingsView] = useState<PostureFindingsViewMode>('grouped');
  const [findingsIncludeSiteItems, setFindingsIncludeSiteItems] = useState(routeState.findingsIncludeSiteItems);
  const [findingsSearch, setFindingsSearch] = useState(routeState.findingsSearch);
  const [findingsHotspotsOnly, setFindingsHotspotsOnly] = useState(routeState.findingsHotspotsOnly);
  const [findingsHasEvidenceOnly, setFindingsHasEvidenceOnly] = useState(routeState.findingsHasEvidenceOnly);
  const [findingsHasRemediationOnly, setFindingsHasRemediationOnly] = useState(routeState.findingsHasRemediationOnly);
  const [findingsHasRecommendationOnly, setFindingsHasRecommendationOnly] = useState(routeState.findingsHasRecommendationOnly);
  const [findingsSeverityFilter, setFindingsSeverityFilter] = useState(routeState.findingsSeverityFilter);
  const [findingsDomainFilter, setFindingsDomainFilter] = useState(routeState.findingsDomainFilter);
  const [findingsSort, setFindingsSort] = useState<'severity_desc' | 'requested_desc' | 'domain_asc'>(routeState.findingsSort);
  const [linkedFindingRuleIds, setLinkedFindingRuleIds] = useState<string[]>([]);
  const [linkedFindingLabel, setLinkedFindingLabel] = useState<string | null>(null);
  const [findingsPage, setFindingsPage] = useState<number>(1);
  const [findingsPageSize, setFindingsPageSize] = useState<number>(DEFAULT_TABLE_PAGE_SIZE);
  const [runsStatusFilter, setRunsStatusFilter] = useState(routeState.runsStatusFilter);
  const [runsTriggerFilter, setRunsTriggerFilter] = useState(routeState.runsTriggerFilter);
  const [runsSort, setRunsSort] = useState<'requested_desc' | 'score_desc' | 'status_asc'>(routeState.runsSort);
  const [runsPage, setRunsPage] = useState<number>(1);
  const [runsPageSize, setRunsPageSize] = useState<number>(DEFAULT_TABLE_PAGE_SIZE);
  const [jobsStatusFilter, setJobsStatusFilter] = useState(routeState.jobsStatusFilter);
  const [jobsScopeFilter, setJobsScopeFilter] = useState(routeState.jobsScopeFilter);
  const [jobsSort, setJobsSort] = useState<'updated_desc' | 'name_asc' | 'scope_asc'>(routeState.jobsSort);
  const [jobsPage, setJobsPage] = useState<number>(1);
  const [jobsPageSize, setJobsPageSize] = useState<number>(DEFAULT_TABLE_PAGE_SIZE);
  const [jobCreateMode, setJobCreateMode] = useState<'create' | 'duplicate'>('create');
  const [jobDuplicateSourceName, setJobDuplicateSourceName] = useState<string>('');
  const [jobForm, setJobForm] = useState<JobFormState>(() => ({
    name: 'Daily Network Posture',
    scopeType: 'org',
    siteId: '',
    scheduleMode: 'recurring',
    cron: '0 2 * * *',
    status: 'active',
    enabledRulePacks: []
  }));
  const [jobRulePacksDirty, setJobRulePacksDirty] = useState(false);

  const sitesFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => fetchOrgCollection<SiteSummary>(basePath, orgId, 'sites?limit=200');
  }, [basePath, fetchOrgCollection, orgId]);

  const { data: sitesResponse, loading: sitesLoading, error: sitesError } = useApiQuery<CursorListResponse<SiteSummary>>(sitesFetcher, [
    sitesFetcher,
    refreshNonce
  ]);

  const sites = sitesResponse?.items ?? [];
  const siteNameById = useMemo(
    () =>
      sites.reduce<Record<string, string>>((acc, site) => {
        if (site.id) {
          acc[site.id] = site.name?.trim() || `Site ${site.id.slice(0, 8)}`;
        }
        return acc;
      }, {}),
    [sites]
  );

  useEffect(() => {
    if (scopeType !== 'site') {
      return;
    }
    if (selectedSiteId) {
      return;
    }
    const firstSiteId = sites.find((site) => site.id)?.id ?? '';
    if (firstSiteId) {
      setSelectedSiteId(firstSiteId);
    }
  }, [scopeType, selectedSiteId, sites]);

  useEffect(() => {
    setSelectedRunId(null);
  }, [scopeType, selectedSiteId]);

  useEffect(() => {
    setOverviewCoverageExpanded(false);
  }, [scopeType, selectedSiteId]);

  useEffect(() => {
    if (isFindingRoute) {
      return;
    }
    const nextSelectedRunId = new URLSearchParams(location.search).get('selectedRunId')?.trim() || null;
    setSelectedRunId((current) => (current === nextSelectedRunId ? current : nextSelectedRunId));
  }, [isFindingRoute, location.search]);

  useEffect(() => {
    const nextSearch = buildPostureRouteSearch(location.search, {
      scopeType,
      siteId: selectedSiteId,
      selectedRunId: selectedRunId ?? '',
      findingsIncludeSiteItems,
      findingsSearch,
      findingsHotspotsOnly,
      findingsHasEvidenceOnly,
      findingsHasRemediationOnly,
      findingsHasRecommendationOnly,
      findingsSeverityFilter,
      findingsDomainFilter,
      findingsSort,
      runsStatusFilter,
      runsTriggerFilter,
      runsSort,
      jobsStatusFilter,
      jobsScopeFilter,
      jobsSort
    });
    if (nextSearch !== location.search) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
          hash: location.hash
        },
        { replace: true, state: location.state }
      );
    }
  }, [
    findingsIncludeSiteItems,
    findingsDomainFilter,
    findingsHasEvidenceOnly,
    findingsHasRemediationOnly,
    findingsHasRecommendationOnly,
    findingsHotspotsOnly,
    findingsSearch,
    findingsSeverityFilter,
    findingsSort,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    jobsScopeFilter,
    jobsSort,
    jobsStatusFilter,
    runsSort,
    runsStatusFilter,
    runsTriggerFilter,
    selectedRunId,
    scopeType,
    selectedSiteId
  ]);

  const scopeReady = scopeType === 'org' || selectedSiteId.length > 0;

  const rulePackCatalogFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    const catalogUrl = `${basePath.replace(/\/+$/, '')}/posture/rule-packs`;
    return async () => {
      const response = await fetchWithAuthRetry(catalogUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(await readResponseError(response));
      }
      const contentType = response.headers.get('Content-Type') ?? '';
      if (!contentType.toLowerCase().includes('application/json')) {
        throw new Error('Expected JSON response from posture rule-pack catalog.');
      }
      return (await response.json()) as PostureRulePackCatalog;
    };
  }, [basePath, fetchWithAuthRetry, orgId]);

  const {
    data: rulePackCatalog,
    loading: rulePackCatalogLoading,
    error: rulePackCatalogError
  } = useApiQuery<PostureRulePackCatalog>(rulePackCatalogFetcher, [rulePackCatalogFetcher, refreshNonce]);
  const shouldUseFallbackRulePacks = !rulePackCatalog && (!rulePackCatalogFetcher || Boolean(rulePackCatalogError));
  const rulePackDefaultsReady = Boolean(rulePackCatalog) || shouldUseFallbackRulePacks;
  const availableRulePacks = useMemo(() => {
    const items = rulePackCatalog ? rulePackCatalog.items ?? [] : shouldUseFallbackRulePacks ? buildFallbackRulePackDescriptors() : [];
    return items.filter((item, index) => item.id && items.findIndex((candidate) => candidate.id === item.id) === index);
  }, [rulePackCatalog, shouldUseFallbackRulePacks]);
  const selectableRulePacks = useMemo(() => availableRulePacks.filter((item) => isSelectableRulePack(item)), [availableRulePacks]);
  const defaultEnabledRulePacks = useMemo(() => {
    if (!rulePackCatalog) {
      if (!shouldUseFallbackRulePacks) {
        return [];
      }
      return [...DEFAULT_RULE_PACKS];
    }
    return normalizeRulePacks(selectableRulePacks.filter((item) => item.defaultEnabled).map((item) => item.id));
  }, [rulePackCatalog, selectableRulePacks, shouldUseFallbackRulePacks]);
  const rulePackLabelById = useMemo(
    () =>
      availableRulePacks.reduce<Record<string, string>>((acc, item) => {
        acc[item.id] = item.label?.trim() || humanizeToken(item.id);
        return acc;
      }, {}),
    [availableRulePacks]
  );
  useEffect(() => {
    if (!runDrawerOpen || runRulePacksDirty || !rulePackDefaultsReady || runForm.enabledRulePacks.length > 0 || defaultEnabledRulePacks.length === 0) {
      return;
    }
    setRunForm((prev) => (prev.enabledRulePacks.length > 0 ? prev : { enabledRulePacks: [...defaultEnabledRulePacks] }));
  }, [defaultEnabledRulePacks, rulePackDefaultsReady, runDrawerOpen, runForm.enabledRulePacks.length, runRulePacksDirty]);
  useEffect(() => {
    if (!jobDrawerOpen || jobRulePacksDirty || !rulePackDefaultsReady || jobForm.enabledRulePacks.length > 0 || defaultEnabledRulePacks.length === 0) {
      return;
    }
    setJobForm((prev) => (prev.enabledRulePacks.length > 0 ? prev : { ...prev, enabledRulePacks: [...defaultEnabledRulePacks] }));
  }, [defaultEnabledRulePacks, jobDrawerOpen, jobForm.enabledRulePacks.length, jobRulePacksDirty, rulePackDefaultsReady]);
  const overviewFetcher = useMemo(() => {
    if (!orgId || !scopeReady) {
      return null;
    }
    return () => fetchOrgResource<PostureOverview>(basePath, orgId, buildPostureResource('posture/overview', scopeType, selectedSiteId));
  }, [basePath, fetchOrgResource, orgId, scopeReady, scopeType, selectedSiteId]);

  const {
    data: overview,
    loading: overviewLoading,
    error: overviewError,
    backendMissing: overviewBackendMissing
  } = useApiQuery<PostureOverview>(overviewFetcher, [overviewFetcher, refreshNonce]);
  const showGlobalBackendMissing = overviewBackendMissing && scopeType === 'org';
  const showSiteScopeBackendMissing = overviewBackendMissing && scopeType === 'site';
  const orgOverviewFetcher = useMemo(() => {
    if (!orgId || scopeType !== 'site') {
      return null;
    }
    return () => fetchOrgResource<PostureOverview>(basePath, orgId, buildPostureResource('posture/overview', 'org', ''));
  }, [basePath, fetchOrgResource, orgId, scopeType]);
  const { data: orgOverviewData, loading: orgOverviewLoading } = useApiQuery<PostureOverview>(orgOverviewFetcher, [orgOverviewFetcher, refreshNonce]);
  const orgOverview = scopeType === 'org' ? overview : orgOverviewData;

  const deltaFetcher = useMemo(() => {
    if (!orgId || !scopeReady) {
      return null;
    }
    return () => fetchOrgResource<PostureDeltaView>(basePath, orgId, buildPostureResource('posture/delta', scopeType, selectedSiteId));
  }, [basePath, fetchOrgResource, orgId, scopeReady, scopeType, selectedSiteId]);

  const { data: deltaView, loading: deltaLoading, error: deltaError } = useApiQuery<PostureDeltaView>(deltaFetcher, [deltaFetcher, refreshNonce]);
  const summary = overview?.summary;
  const findingsRollup = overview?.findings;
  const findingResource = useMemo(() => buildPostureResource('posture/findings', scopeType, selectedSiteId, {
    view: findingsView === 'grouped' ? 'grouped' : 'records', q: findingsSearch.trim(), sort: findingsSort,
    includeSiteItems: scopeType === 'org' && findingsIncludeSiteItems,
    ruleIds: linkedFindingRuleIds.join(','),
    hotspotsOnly: findingsHotspotsOnly, hasEvidence: findingsHasEvidenceOnly,
    hasRecommendation: findingsHasRecommendationOnly, hasRemediation: findingsHasRemediationOnly,
    severity: findingsSeverityFilter === 'all' ? undefined : findingsSeverityFilter,
    domain: findingsDomainFilter === 'all' ? undefined : findingsDomainFilter,
    limit: findingsPageSize, offset: (findingsPage - 1) * findingsPageSize
  }), [scopeType, selectedSiteId, findingsView, findingsSearch, findingsSort, findingsIncludeSiteItems,
    linkedFindingRuleIds, findingsHotspotsOnly, findingsHasEvidenceOnly, findingsHasRecommendationOnly,
    findingsHasRemediationOnly, findingsSeverityFilter, findingsDomainFilter, findingsPageSize, findingsPage]);
  const findingsFetcher = useMemo(() => !orgId || !scopeReady ? null :
    () => fetchOrgResource<PostureFindingList>(basePath, orgId, findingResource),
    [orgId, scopeReady, fetchOrgResource, basePath, findingResource]);

  const { data: findingsResponse, loading: findingsResponseLoading, error: findingsResponseError } = useApiQuery<PostureFindingList>(findingsFetcher, [
    findingsFetcher,
    refreshNonce
  ]);
  const findings = findingsResponse?.items ?? [];

  const runsFetcher = useMemo(() => {
    if (!orgId || !scopeReady) {
      return null;
    }
    return () =>
      fetchOrgResource<PostureRunList>(
        basePath,
        orgId,
        buildPostureResource('posture/runs', scopeType, selectedSiteId, {
          limit: runsPageSize,
          offset: (runsPage-1)*runsPageSize,
          jobId: selectedJobId || undefined,
          status: runsStatusFilter === 'all' ? undefined : runsStatusFilter,
          triggerType: runsTriggerFilter === 'all' ? undefined : runsTriggerFilter,
          sortBy: runsSort === 'score_desc' ? 'score' : runsSort === 'status_asc' ? 'status' : 'requestedAt',
          sortDesc: runsSort !== 'status_asc'
        })
      );
  }, [basePath, fetchOrgResource, orgId, scopeReady, scopeType, selectedSiteId, runsPage, runsPageSize, runsStatusFilter, runsTriggerFilter, runsSort, selectedJobId]);

  const { data: runsResponse, loading: runsLoading, error: runsError } = useApiQuery<PostureRunList>(runsFetcher, [runsFetcher, refreshNonce]);
  const runs = runsResponse?.items ?? [];
  const runStatusOptions = ['pending', 'running', 'cancel_requested', 'cancelled', 'completed', 'completed_with_gaps', 'failed'];
  const runTriggerOptions = ['periodic', 'event_driven', 'on_demand'];
  const scopedRuns = useMemo(() => {
    if (!selectedJobId) {
      return runs;
    }
    return runs.filter((item) => item.jobId === selectedJobId);
  }, [runs, selectedJobId]);
  const filteredRuns = scopedRuns;

  const runsFiltersActive = runsStatusFilter !== 'all' || runsTriggerFilter !== 'all' || runsSort !== 'requested_desc';

  const jobsFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => {
      const params = new URLSearchParams({ limit: String(jobsPageSize), offset: String((jobsPage - 1) * jobsPageSize), sort: jobsSort });
      if (jobsStatusFilter !== 'all') params.set('status', jobsStatusFilter);
      if (jobsScopeFilter !== 'all') params.set('scopeType', jobsScopeFilter);
      return fetchOrgResource<PostureJobList>(basePath, orgId, `posture/jobs?${params}`);
    };
  }, [basePath, fetchOrgResource, orgId, jobsPage, jobsPageSize, jobsStatusFilter, jobsScopeFilter, jobsSort]);

  const { data: jobsResponse, loading: jobsLoading, error: jobsError } = useApiQuery<PostureJobList>(jobsFetcher, [jobsFetcher, refreshNonce]);

  useEffect(() => {
    if (!jobsResponse) {
      return;
    }
    setJobsState(jobsResponse);
  }, [jobsResponse]);

  useEffect(() => {
    setLocallyDeletedJobIds(new Set());
  }, [orgId]);

  const jobs = (jobsState?.items ?? jobsResponse?.items ?? []).filter((job) => !locallyDeletedJobIds.has(job.jobId));





  const jobStatusOptions = ['active', 'paused'];
  const filteredJobs = jobs;

  const jobsFiltersActive = jobsStatusFilter !== 'all' || jobsScopeFilter !== 'all' || jobsSort !== 'updated_desc';

  const upsertJobInState = (job: PostureJobView) => {
    setJobsState((prev) => {
      const existing = prev ?? jobsResponse ?? { items: [], total: 0, limit: JOBS_LIMIT, offset: 0 };
      const nextItems = [job, ...existing.items.filter((item) => item.jobId !== job.jobId)].slice(0, existing.limit || JOBS_LIMIT);
      return {
        ...existing,
        items: nextItems,
        total: Math.max(existing.total, nextItems.length)
      };
    });
  };

  const removeJobFromState = (jobId: string) => {
    setJobsState((prev) => {
      const existing = prev ?? jobsResponse;
      if (!existing) {
        return prev;
      }
      const nextItems = existing.items.filter((item) => item.jobId !== jobId);
      return {
        ...existing,
        items: nextItems,
        total: Math.max(0, existing.total - (existing.items.length - nextItems.length))
      };
    });
  };

  const effectiveRunId = isFindingRoute ? routeRunId : selectedRunId;

  const selectedRunFetcher = useMemo(() => {
    if (!orgId || !effectiveRunId) {
      return null;
    }
    return () => fetchOrgResource<PostureRunView>(basePath, orgId, `posture/runs/${encodeURIComponent(effectiveRunId)}`);
  }, [basePath, effectiveRunId, fetchOrgResource, orgId]);

  const { data: selectedRun, loading: selectedRunLoading, error: selectedRunError } = useApiQuery<PostureRunView>(selectedRunFetcher, [
    selectedRunFetcher,
    refreshNonce
  ]);
  const selectedRunChildRunsFetcher = useMemo(() => {
    if (!orgId || !selectedRun?.runId || selectedRun.scopeType !== 'org') {
      return null;
    }
    return () => fetchAllChildRuns(basePath, orgId, selectedRun.runId, fetchOrgResource);
  }, [basePath, fetchOrgResource, orgId, selectedRun?.runId, selectedRun?.scopeType]);

  const {
    data: selectedRunChildRunsResponse,
    loading: selectedRunChildRunsLoading,
    error: selectedRunChildRunsError
  } = useApiQuery<PostureRunList>(selectedRunChildRunsFetcher, [selectedRunChildRunsFetcher, refreshNonce]);
  const selectedRunChildRuns = selectedRunChildRunsResponse?.items ?? [];
  const overviewLinkableFindings = findings;
  const effectiveFindings = findings;
  const findingsLoading = findingsResponseLoading;
  const findingsError = findingsResponseError;
  const findingsTotal = findingsResponse?.total ?? 0;
  const findingSeverityOptions = useMemo(() => {
    return uniqueTokens([...effectiveFindings.map((item) => item.finding.severity), ...Object.keys(findingsRollup?.bySeverity ?? {}), ...(findingsSeverityFilter !== 'all' ? [findingsSeverityFilter] : [])]);
  }, [effectiveFindings, findingsRollup?.bySeverity, findingsSeverityFilter]);
  const findingDomainOptions = useMemo(() => {
    return uniqueTokens([...effectiveFindings.map((item) => findingCategoryKey(item.finding)), ...Object.keys(findingsRollup?.byDomain ?? {}), ...(findingsDomainFilter !== 'all' ? [findingsDomainFilter] : [])]);
  }, [effectiveFindings, findingsRollup?.byDomain, findingsDomainFilter]);

  const filteredFindings = findings;
  const filteredFindingGroups = findingsResponse?.groups ?? [];
  const findingsGroupedViewActive = findingsView === 'grouped';
  const findingsFiltersActive =
    linkedFindingRuleIds.length > 0 ||
    (scopeType === 'org' && findingsIncludeSiteItems) ||
    findingsSearch.trim().length > 0 ||
    findingsHotspotsOnly ||
    findingsHasEvidenceOnly ||
    findingsHasRemediationOnly ||
    findingsHasRecommendationOnly ||
    findingsSeverityFilter !== 'all' ||
    findingsDomainFilter !== 'all' ||
    findingsSort !== 'severity_desc';
  const findingsPageCount = Math.max(1, Math.ceil(findingsTotal / findingsPageSize));
  const pagedFindings = filteredFindings;
  const pagedFindingGroups = filteredFindingGroups;
  const findingsPaginationMeta = {totalCount: findingsTotal, pageCount: findingsPageCount,
    hasPrevPage: findingsPage > 1, hasNextPage: findingsPage < findingsPageCount, exactTotal: findingsTotal};
  const findingsSummary = findingsGroupedViewActive
    ? `${formatNumber(findingsTotal)} rule group${findingsTotal === 1 ? '' : 's'} covering ${formatNumber(findingsResponse?.findingTotal ?? 0)} findings`
    : formatVisibleRowsSummary(findingsPage, findingsPageSize, pagedFindings.length, findingsTotal, 'findings');

  useEffect(() => {
    setFindingsPage(1);
  }, [
    scopeType,
    selectedSiteId,
    findingsView,
    findingsIncludeSiteItems,
    findingsSearch,
    findingsHotspotsOnly,
    findingsHasEvidenceOnly,
    findingsHasRemediationOnly,
    findingsHasRecommendationOnly,
    findingsSeverityFilter,
    findingsDomainFilter,
    findingsSort
  ]);

  useEffect(() => {
    setFindingsPage((current) => Math.min(current, findingsPageCount));
  }, [findingsPageCount]);

  async function requestOrgJson<T>(resource: string, init: RequestInit): Promise<T>;
  async function requestOrgJson<T>(resource: string, init: RequestInit, options: PostureSuccessResponseWithoutJson): Promise<T | null>;
  async function requestOrgJson<T>(
    resource: string,
    init: RequestInit,
    options?: PostureSuccessResponseOptions
  ): Promise<T | null> {
    if (!orgId) {
      throw new Error('No organization selected.');
    }
    const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, resource), init);
    if (!response.ok) {
      throw new Error(await readResponseError(response));
    }
    return readPostureSuccessResponse<T>(response, options);
  }

  const currentScopeLabel = scopeType === 'org' ? orgDisplayName : siteNameById[selectedSiteId] ?? 'Selected site';

  const openManualRunDrawer = () => {
    setRunRulePacksDirty(false);
    setRunForm({ enabledRulePacks: [...defaultEnabledRulePacks] });
    setRunSubmitError(null);
    setRunSubmitSuccess(null);
    setReportActionStatus(null);
    setRunDrawerOpen(true);
  };

  const openCreateJobDrawer = (seed?: PostureJobView) => {
    const seedSiteId = seed ? inferJobSiteId(seed) : '';
    const seedScheduleMode = seed?.schedule?.mode === 'manual' ? 'manual' : 'recurring';
    setJobRulePacksDirty(false);
    setJobCreateMode(seed ? 'duplicate' : 'create');
    setJobDuplicateSourceName(seed?.name?.trim() || '');
    setJobForm({
      name: seed?.name?.trim() ? `${seed.name.trim()} copy` : scopeType === 'site' ? `Daily Posture - ${siteNameById[selectedSiteId] ?? 'Site'}` : 'Daily Network Posture',
      scopeType: seed?.scopeType === 'site' ? 'site' : 'org',
      siteId: seed?.scopeType === 'site' ? seedSiteId : scopeType === 'site' ? selectedSiteId : '',
      scheduleMode: seedScheduleMode,
      cron: seedScheduleMode === 'recurring' ? seed?.schedule?.cron?.trim() || '0 2 * * *' : '0 2 * * *',
      status: seed?.status === 'paused' ? 'paused' : 'active',
      enabledRulePacks: seed?.enabledRulePacks?.length ? normalizeRulePacks(seed.enabledRulePacks) : [...defaultEnabledRulePacks]
    });
    setJobSaveError(null);
    setJobSaveSuccess(null);
    setJobSaveTone(null);
    setJobDrawerOpen(true);
  };

  const handleStartManualRun = async () => {
    if (!orgId || !selectedSiteId) {
      setRunSubmitError('Pick a site before starting a manual posture run.');
      return;
    }
    if (!rulePackDefaultsReady) {
      setRunSubmitError('Wait for the rule-pack catalog to finish loading before starting a run.');
      return;
    }
    setRunSubmitting(true);
    setRunSubmitError(null);
    setRunSubmitSuccess(null);
    try {
      const result = await requestOrgJson<PostureRunView>('posture/runs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          scopeType: 'site',
          scopeId: selectedSiteId,
          enabledRulePacks: normalizeRulePacks(runForm.enabledRulePacks)
        })
      });
      setRunDrawerOpen(false);
      setRunSubmitSuccess(`Started manual posture run ${shortRunId(result.runId)}.`);
      setSelectedRunId(result.runId);
      recordRunFeedback(result.runId, 'Manual run started.');
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      setRunSubmitError(error instanceof Error ? error.message : 'Failed to start posture run.');
    } finally {
      setRunSubmitting(false);
    }
  };

  const handleCancelRun = async (runId: string) => {
    setRunCancelBusyId(runId);
    setRunCancelError(null);
    try {
      await requestOrgJson<PostureRunView>(
        `posture/runs/${encodeURIComponent(runId)}/cancel`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          }
        },
        { requireJson: false }
      );
      if (selectedRunId === runId) {
        setSelectedRunId(runId);
      }
      recordRunFeedback(runId, 'Cancel requested.', 'warning');
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      setRunCancelError(error instanceof Error ? error.message : 'Failed to cancel posture run.');
    } finally {
      setRunCancelBusyId(null);
    }
  };

  const handleCopyReport = async (run: PostureRunView) => {
    const content = run.report?.content?.trim();
    if (!content) {
      setReportActionStatus('No stored report content is available to copy.');
      return;
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = content;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setReportActionStatus('Report copied to clipboard.');
      window.setTimeout(() => setReportActionStatus(null), 2000);
    } catch (error) {
      setReportActionStatus(error instanceof Error ? error.message : 'Failed to copy report.');
      window.setTimeout(() => setReportActionStatus(null), 2500);
    }
  };

  const handleDownloadReport = (run: PostureRunView) => {
    const content = run.report?.content?.trim();
    if (!content) {
      setReportActionStatus('No stored report content is available to download.');
      return;
    }
    const extension = normalizeToken(run.report?.format) === 'markdown' ? 'md' : 'txt';
    const filename = `posture-run-${shortRunId(run.runId)}-report.${extension}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
    setReportActionStatus(`Report downloaded as ${filename}.`);
    window.setTimeout(() => setReportActionStatus(null), 2000);
  };

  const handleCreateJob = async () => {
    if (!orgId) {
      return;
    }
    if (!rulePackDefaultsReady) {
      setJobSaveError('Wait for the rule-pack catalog to finish loading before creating a job.');
      return;
    }
    if (jobForm.scopeType === 'site' && !jobForm.siteId) {
      setJobSaveError('Pick a site for a site-scoped posture job.');
      return;
    }
    setJobSaving(true);
    setJobSaveError(null);
    setJobSaveSuccess(null);
    setJobSaveTone(null);
    try {
      const payload = {
        name: jobForm.name.trim(),
        scopeType: jobForm.scopeType,
        scopeSelector: jobForm.scopeType === 'site' ? { siteId: jobForm.siteId } : {},
        schedule: {
          mode: jobForm.scheduleMode,
          cron: jobForm.scheduleMode === 'recurring' ? jobForm.cron.trim() : '',
          triggerScope: jobForm.scopeType
        },
        enabledRulePacks: normalizeRulePacks(jobForm.enabledRulePacks),
        status: jobForm.status
      };
      const result = await requestOrgJson<PostureJobView>('posture/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      upsertJobInState(result);
      setJobDrawerOpen(false);
      setJobSaveSuccess(
        jobCreateMode === 'duplicate'
          ? `Duplicated posture job ${result.name || shortRunId(result.jobId)}${jobDuplicateSourceName ? ` from "${jobDuplicateSourceName}"` : ''}.`
          : `Created posture job ${result.name || shortRunId(result.jobId)}.`
      );
      setJobSaveTone('success');
      setSelectedJobId(result.jobId);
      recordJobFeedback(
        result.jobId,
        jobCreateMode === 'duplicate' ? `Duplicated from ${jobDuplicateSourceName || 'another job'}.` : 'Created just now.'
      );
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      setJobSaveError(error instanceof Error ? error.message : 'Failed to create posture job.');
    } finally {
      setJobSaving(false);
    }
  };

  const handleToggleJobStatus = async (job: PostureJobView) => {
    const nextStatus = job.status === 'paused' ? 'active' : 'paused';
    setJobToggleBusyId(job.jobId);
    setJobActionError(null);
    setJobSaveSuccess(null);
    setJobSaveTone(null);
    try {
      await requestOrgJson<PostureJobView>(
        `posture/jobs/${encodeURIComponent(job.jobId)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            status: nextStatus
          })
        },
        { requireJson: false }
      );
      upsertJobInState({
        ...job,
        status: nextStatus,
        updatedAt: new Date().toISOString()
      });
      setJobSaveSuccess(`Job "${job.name}" is now ${nextStatus}.`);
      setJobSaveTone(nextStatus === 'paused' ? 'warning' : 'success');
      recordJobFeedback(job.jobId, nextStatus === 'paused' ? 'Paused just now.' : 'Activated just now.', nextStatus === 'paused' ? 'warning' : 'success');
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      setJobActionError(error instanceof Error ? error.message : 'Failed to update posture job.');
    } finally {
      setJobToggleBusyId(null);
    }
  };

  const handleTriggerJob = async (job: PostureJobView) => {
    if (!orgId) {
      return;
    }
    setJobTriggerBusyId(job.jobId);
    setJobActionError(null);
    setJobSaveSuccess(null);
    setJobSaveTone(null);
    try {
      const result = await requestOrgJson<PostureJobTriggerResult>(`posture/jobs/${encodeURIComponent(job.jobId)}/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({})
      });
      const requested = Number.isFinite(result.requested) ? result.requested : 0;
      const started = Number.isFinite(result.started) ? result.started : 0;
      const skippedActive = Number.isFinite(result.skippedActive) ? result.skippedActive : 0;
      const failedSiteCount = Array.isArray(result.failedSites) ? result.failedSites.length : 0;
      const primaryRun = result.orgRun ?? result.runs?.[0] ?? null;
      if (primaryRun?.runId) {
        setSelectedJobId(null);
        setActiveJobId(null);
        setSelectedRunId(primaryRun.runId);
        recordRunFeedback(primaryRun.runId, 'Started from saved job template.');
      }
      const summary =
        failedSiteCount > 0
          ? `Started posture job "${job.name}" with issues (${formatNumber(started)} of ${formatNumber(requested)} runs started, ${formatNumber(failedSiteCount)} site failure${failedSiteCount === 1 ? '' : 's'} recorded).`
          : started > 0
          ? `Started posture job "${job.name}" (${formatNumber(started)} of ${formatNumber(requested)} run${requested === 1 ? '' : 's'}).`
          : skippedActive > 0
            ? `Skipped posture job "${job.name}" because an equivalent run is already active.`
            : `No runs were started for posture job "${job.name}".`;
      setJobSaveSuccess(summary);
      setJobSaveTone(started > 0 && failedSiteCount === 0 ? 'success' : 'warning');
      recordJobFeedback(
        job.jobId,
        failedSiteCount > 0
          ? `Run started with ${formatNumber(failedSiteCount)} recorded site failure${failedSiteCount === 1 ? '' : 's'}.`
          : started > 0
            ? `Run started (${formatNumber(started)}/${formatNumber(requested)}).`
            : 'No new runs started.',
        started > 0 && failedSiteCount === 0 ? 'success' : 'warning'
      );
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      setJobActionError(error instanceof Error ? error.message : 'Failed to start posture job.');
    } finally {
      setJobTriggerBusyId(null);
    }
  };

  const handleDeleteJob = (job: PostureJobView) => {
    if (jobDeleteBusyId) {
      return;
    }
    setPendingDeleteJob(job);
  };

  const confirmDeleteJob = async () => {
    const job = pendingDeleteJob;
    if (!job || jobDeleteBusyId) {
      return;
    }
    setJobDeleteBusyId(job.jobId);
    setJobActionError(null);
    setJobSaveSuccess(null);
    setJobSaveTone(null);
    try {
      const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId ?? '', `posture/jobs/${encodeURIComponent(job.jobId)}`), {
        method: 'DELETE',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(await readResponseError(response));
      }
      if (selectedJobId === job.jobId) {
        setSelectedJobId(null);
      }
      if (activeJobId === job.jobId) {
        setActiveJobId(null);
      }
      setLocallyDeletedJobIds((current) => new Set([...current, job.jobId]));
      removeJobFromState(job.jobId);
      setPendingDeleteJob(null);
      setJobSaveSuccess(`Deleted job "${job.name}".`);
      setJobSaveTone('success');
      setJobDeleteToast({ tone: 'success', message: `Posture job "${job.name}" deleted successfully.` });
      setRefreshNonce((value) => value + 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete posture job.';
      setJobActionError(message);
      setJobDeleteToast({ tone: 'error', message });
    } finally {
      setJobDeleteBusyId(null);
    }
  };

  const findingsColumns = useMemo<Array<ListWorkspaceColumn<PostureFindingRecord>>>(
    () => [
      {
        key: 'title',
        header: 'Finding',
        width: '28rem',
        render: (row) => (
          <div className="posture-table-cell">
            <span className="posture-table-cell__title">{findingDisplayTitle(row.finding)}</span>
            <span className="posture-table-cell__subtle">{findingDisplayDetail(row.finding)}</span>
          </div>
        )
      },
      {
        key: 'domain',
        header: 'Category',
        width: '12rem',
        render: (row) => <span className="ui-badge ui-badge--ghost">{findingCategoryLabel(row.finding)}</span>
      },
      {
        key: 'severity',
        header: 'Severity',
        width: '8rem',
        render: (row) => <span className={severityBadgeClass(row.finding.severity)}>{humanizeToken(row.finding.severity)}</span>
      },
      {
        key: 'scope',
        header: 'Scope',
        width: '12rem',
        render: (row) => renderScopeLabel(row.finding.scopeType, row.finding.scopeId, siteNameById, orgDisplayName, deviceSummaryById)
      },
      {
        key: 'action',
        header: 'Recommended action',
        width: '16rem',
        render: (row) => truncateText(findingDisplayAction(row.finding) || '—', 120)
      },
      {
        key: 'evidence',
        header: 'Evidence',
        width: '7rem',
        render: (row) => formatNumber(row.finding.evidence?.length ?? 0)
      },
      {
        key: 'requestedAt',
        header: 'Run time',
        width: '10rem',
        render: (row) => formatDateTime(row.requestedAt)
      }
    ],
    [deviceSummaryById, orgDisplayName, siteNameById]
  );
  const findingGroupColumns = useMemo<Array<ListWorkspaceColumn<PostureFindingGroupRecord>>>(
    () => [
      {
        key: 'title',
        header: 'Rule / fact',
        width: '34rem',
        render: (row) => (
          <FindingGroupMembers row={row} resource={findingResource}
            fetchPage={(resource) => fetchOrgResource<PostureFindingList>(basePath, orgId!, resource)}
            onSelect={setSelectedFinding}
            scopeLabel={(record) => resolveScopeLabel(record.finding.scopeType, record.finding.scopeId, siteNameById, orgDisplayName, deviceSummaryById)} />
        )
      },
      {
        key: 'domain',
        header: 'Category',
        width: '12rem',
        render: (row) => <span className="ui-badge ui-badge--ghost">{row.domain}</span>
      },
      {
        key: 'severity',
        header: 'Highest severity',
        width: '10rem',
        render: (row) => <span className={severityBadgeClass(row.severity)}>{humanizeToken(row.severity)}</span>
      },
      {
        key: 'affected',
        header: 'Affected scopes',
        width: '8rem',
        render: (row) => formatNumber(row.scopeCount)
      },
      {
        key: 'action',
        header: 'Recommended action',
        width: '16rem',
        render: (row) => truncateText(row.recommendedAction || 'Multiple actions', 120)
      },
      {
        key: 'requestedAt',
        header: 'Latest run',
        width: '10rem',
        render: (row) => formatDateTime(row.requestedAt)
      }
    ],
    [deviceSummaryById, orgDisplayName, siteNameById, findingResource, fetchOrgResource, basePath, orgId]
  );

  const runColumns = useMemo<Array<ListWorkspaceColumn<PostureRunView>>>(
    () => [
      {
        key: 'name',
        header: 'Run',
        width: '18rem',
        render: (row) => (
          <div className="posture-table-cell">
            <span className="posture-table-cell__title">{resolveRunDisplayName(row, siteNameById, orgDisplayName)}</span>
            <span className="posture-table-cell__subtle">{resolveScopeLabel(row.scopeType, row.scopeId, siteNameById, orgDisplayName)}</span>
            {runFeedbackById[row.runId] ? (
              <span className={`posture-chip ${runFeedbackById[row.runId].tone === 'warning' ? 'posture-chip--warning' : 'posture-chip--success'}`}>
                {runFeedbackById[row.runId].message} {formatRelativeTime(runFeedbackById[row.runId].at)}
              </span>
            ) : null}
          </div>
        )
      },
      {
        key: 'triggerType',
        header: 'Trigger',
        width: '8rem',
        render: (row) => <span className="ui-badge ui-badge--ghost">{humanizeToken(row.triggerType)}</span>
      },
      {
        key: 'job',
        header: 'Job',
        width: '14rem',
        render: (row) =>
          row.jobId ? (
            <button
              type="button"
              className="posture-chip posture-chip--neutral"
              onClick={(event) => {
                event.stopPropagation();
                if (!row.jobId) {
                  return;
                }
                openJobDetail(row.jobId, { select: true });
              }}>
              {resolveRunJobLabel(row.jobId, jobs)}
            </button>
          ) : (
            'Manual'
          )
      },
      {
        key: 'status',
        header: 'Status',
        width: '9rem',
        render: (row) => (
          <div className="posture-table-cell">
            <span className={statusBadgeClass(row.status)}>{humanizeToken(row.status)}</span>
            {describeRunLifecycle(row) ? <span className="posture-table-cell__subtle">{describeRunLifecycle(row)}</span> : null}
          </div>
        )
      },
      {
        key: 'coverage',
        header: 'Coverage',
        width: '9rem',
        render: (row) => <span className={coverageBadgeClass(row.coverageStatus)}>{humanizeToken(row.coverageStatus)}</span>
      },
      {
        key: 'score',
        header: 'Score',
        width: '7rem',
        render: (row) => formatNumber(row.summary?.overallScore)
      },
      {
        key: 'findings',
        header: 'Findings',
        width: '7rem',
        render: (row) => formatNumber(row.findings?.length ?? totalFindingCount(row.summary?.findingCounts))
      },
      {
        key: 'requestedAt',
        header: 'Requested',
        width: '11rem',
        render: (row) => formatDateTime(row.requestedAt)
      }
    ],
    [jobs, orgDisplayName, runFeedbackById, siteNameById]
  );
  const runsPageCount = Math.max(1, Math.ceil((runsResponse?.total ?? runs.length) / runsPageSize));
  const pagedRuns = filteredRuns;

  useEffect(() => {
    setRunsPage(1);
  }, [scopeType, selectedSiteId, runsStatusFilter, runsTriggerFilter, runsSort, selectedJobId]);

  useEffect(() => {
    setRunsPage((current) => Math.min(current, runsPageCount));
  }, [runsPageCount]);

  const jobColumns = useMemo<Array<ListWorkspaceColumn<PostureJobView>>>(
    () => [
      {
        key: 'name',
        header: 'Job',
        width: '18rem',
        render: (row) => (
          <div className="posture-table-cell">
            <button
              type="button"
              className="posture-link-button posture-table-cell__title-button"
              onClick={(event) => {
                event.stopPropagation();
                openJobDetail(row.jobId, { select: true });
              }}>
              <span className="posture-table-cell__title">{row.name || shortRunId(row.jobId)}</span>
            </button>
            <span className="posture-table-cell__subtle">{resolveJobScopeLabel(row, siteNameById, orgDisplayName)}</span>
            {jobFeedbackById[row.jobId] ? (
              <span className={`posture-chip ${jobFeedbackById[row.jobId].tone === 'warning' ? 'posture-chip--warning' : 'posture-chip--success'}`}>
                {jobFeedbackById[row.jobId].message} {formatRelativeTime(jobFeedbackById[row.jobId].at)}
              </span>
            ) : null}
          </div>
        )
      },
      {
        key: 'schedule',
        header: 'Schedule',
        width: '12rem',
        render: (row) => formatSchedule(row.schedule)
      },
      {
        key: 'status',
        header: 'Status',
        width: '8rem',
        render: (row) => <span className={jobStatusBadgeClass(row.status)}>{humanizeToken(row.status)}</span>
      },
      {
        key: 'packs',
        header: 'Rule packs',
        width: '16rem',
        render: (row) => formatRulePackSummary(row.enabledRulePacks, defaultEnabledRulePacks, rulePackLabelById)
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        width: '11rem',
        render: (row) => formatDateTime(row.updatedAt)
      }
    ],
    [defaultEnabledRulePacks, jobFeedbackById, orgDisplayName, rulePackLabelById, siteNameById]
  );
  const jobsPageCount = Math.max(1, Math.ceil((jobsResponse?.total ?? jobs.length) / jobsPageSize));
  const pagedJobs = filteredJobs;

  useEffect(() => {
    setJobsPage(1);
  }, [jobsStatusFilter, jobsScopeFilter, jobsSort]);

  useEffect(() => {
    setJobsPage((current) => Math.min(current, jobsPageCount));
  }, [jobsPageCount]);

  const overviewDelta = overview?.delta;
  const currentDeltaStatus = normalizeToken(deltaView?.deltaStatus ?? overviewDelta?.status);
  const currentDeltaCounts = {
    new: deltaView?.newFindingCount ?? overviewDelta?.newFindings ?? 0,
    persisting: deltaView?.persistingFindingCount ?? overviewDelta?.persistingFindings ?? 0,
    cleared: deltaView?.clearedFindingCount ?? overviewDelta?.clearedFindings ?? 0
  };
  const currentDeltaAvailable = Boolean(deltaView || overviewDelta);
  const orgFindingsTotal = orgOverview?.totalFindingsInOrg ?? orgOverview?.findings?.totalFindings;
  const domainScores = Object.entries(summary?.domainScores ?? {});
  const priorityActionItems = useMemo(() => {
    const explicitItems = firstNonEmptyArray(overview?.priorityActionItems, summary?.priorityActionItems);
    if (explicitItems.length > 0) {
      return explicitItems;
    }
    return firstNonEmptyArray(overview?.priorityActions, summary?.priorityActions).map((label) => ({
      label,
      kind: 'priority_action'
    }));
  }, [overview?.priorityActionItems, overview?.priorityActions, summary?.priorityActionItems, summary?.priorityActions]);
  const strengthItems = useMemo(() => {
    const explicitItems = firstNonEmptyArray(overview?.strengthItems, summary?.strengthItems);
    if (explicitItems.length > 0) {
      return explicitItems;
    }
    return firstNonEmptyArray(overview?.strengths, summary?.strengths).map((label) => ({
      label,
      kind: 'strength'
    }));
  }, [overview?.strengthItems, overview?.strengths, summary?.strengthItems, summary?.strengths]);
  const overviewCoverageNotes = useMemo(
    () => (summary?.coverageNotes?.length ? summary.coverageNotes : summarizeCoverageNotes(summary, siteNameById)),
    [siteNameById, summary]
  );
  const selectedJob = useMemo(() => jobs.find((job) => job.jobId === selectedJobId) ?? null, [jobs, selectedJobId]);
  const activeJob = useMemo(() => jobs.find((job) => job.jobId === activeJobId) ?? null, [activeJobId, jobs]);
  const selectedRunJob = useMemo(() => (selectedRun?.jobId ? jobs.find((job) => job.jobId === selectedRun.jobId) ?? null : null), [jobs, selectedRun?.jobId]);
  const selectedRunFeedback = selectedRun ? runFeedbackById[selectedRun.runId] ?? null : null;
  const selectedJobFeedback = activeJob ? jobFeedbackById[activeJob.jobId] ?? null : null;
  const selectedRunCoverageNotes = useMemo(
    () =>
      selectedRun?.summary?.coverageNotes?.length
        ? selectedRun.summary.coverageNotes
        : summarizeCoverageNotes(selectedRun?.summary, siteNameById),
    [selectedRun?.summary, siteNameById]
  );
  const selectedRunLifecycle = useMemo(() => summarizeRunLifecycleDetails(selectedRun), [selectedRun]);
  const selectedRunPersistedRulePacks = useMemo(
    () => (selectedRun?.enabledRulePacks?.length ? normalizeRulePacks(selectedRun.enabledRulePacks) : []),
    [selectedRun?.enabledRulePacks]
  );
  const selectedRunCurrentJobRulePacks = useMemo(
    () => (selectedRunJob?.enabledRulePacks?.length ? normalizeRulePacks(selectedRunJob.enabledRulePacks) : []),
    [selectedRunJob?.enabledRulePacks]
  );
  const selectedRunFindingRecords = useMemo(() => buildRunFindingRecords(selectedRun, selectedRunChildRuns), [selectedRun, selectedRunChildRuns]);
  const selectedRunChildRunCount = selectedRunChildRuns.length;
  const selectedRunAggregatesChildFindings =
    Boolean(selectedRun?.scopeType === 'org') &&
    selectedRunFindingRecords.length > 0 &&
    selectedRunChildRunCount > 0 &&
    !(selectedRun?.findings?.length ?? 0);
  const routeFinding = useMemo(() => {
    if (!isFindingRoute || !selectedRunFindingRecords.length) {
      return null;
    }
    return selectedRunFindingRecords.find((item) => findingRouteKey(item.finding) === routeFindingKey) ?? null;
  }, [isFindingRoute, routeFindingKey, selectedRunFindingRecords]);
  const activeFinding = isFindingRoute ? routeFinding : selectedFinding;
  const visibleDeviceScopeIds = useMemo(() => {
    const ids = new Set<string>();
    const collect = (record?: PostureFindingRecord | null) => {
      if (!record || normalizeToken(record.finding.scopeType) !== 'device') {
        return;
      }
      const scopeId = record.finding.scopeId.trim();
      if (scopeId) {
        ids.add(scopeId);
      }
    };
    pagedFindings.forEach(collect);
    selectedRunFindingRecords.forEach(collect);
    collect(activeFinding);
    return [...ids];
  }, [activeFinding, pagedFindings, selectedRunFindingRecords]);

  useEffect(() => {
    setDeviceSummaryById({});
  }, [orgId]);

  useEffect(() => {
    if (!orgId || visibleDeviceScopeIds.length === 0) {
      return;
    }
    const missingIds = visibleDeviceScopeIds.filter((id) => !(id in deviceSummaryById));
    if (missingIds.length === 0) {
      return;
    }
    let cancelled = false;
    void Promise.all(
      missingIds.map(async (deviceId) => {
        try {
          const device = await fetchOrgResource<PostureDeviceSummary>(basePath, orgId, `devices/${encodeURIComponent(deviceId)}`);
          return [deviceId, device] as const;
        } catch {
          return [deviceId, null] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) {
        return;
      }
      setDeviceSummaryById((prev) => {
        const next = { ...prev };
        entries.forEach(([deviceId, summary]) => {
          next[deviceId] = summary;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [basePath, deviceSummaryById, fetchOrgResource, orgId, visibleDeviceScopeIds]);
  const postureTabs = useMemo(
    () => [
      { id: 'summary', label: 'Scope summary' },
      { id: 'findings', label: 'Findings' },
      { id: 'setup', label: 'Runs & setup' }
    ],
    []
  );

  const topSlot = (
    <div className="posture-top">
      <div className="posture-scope-bar">
        <div className="posture-scope-bar__controls">
          <label className="posture-field">
            <span>Scope</span>
            <select
              value={scopeType}
              onChange={(event) => {
                const next = event.target.value === 'site' ? 'site' : 'org';
                setScopeType(next);
              }}>
              <option value="org">Organization</option>
              <option value="site">Site</option>
            </select>
          </label>
          {scopeType === 'site' ? (
            <label className="posture-field posture-field--wide">
              <span>Site</span>
              <select value={selectedSiteId} onChange={(event) => setSelectedSiteId(event.target.value)} disabled={sitesLoading || sites.length === 0}>
                {sites.length === 0 ? <option value="">No sites available</option> : null}
                {sites.map((site) => (
                  <option key={site.id ?? site.name} value={site.id}>
                    {site.name || site.id}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        <div className="posture-scope-bar__actions">
          {scopeType === 'site' ? <span className="posture-scope-bar__context">{currentScopeLabel}</span> : null}
          <span className="posture-status-strip__text">
            {overview?.requestedAt ? `Latest run ${formatRelativeTime(overview.requestedAt)}` : 'Run history not loaded yet'}
          </span>
          <button type="button" className="shortcut-button" onClick={() => setRefreshNonce((value) => value + 1)}>
            <span className="shortcut-button__icon">
              <RefreshIcon size={16} />
            </span>
            Refresh
          </button>
        </div>
      </div>

      <div className="kpi-grid posture-top__metrics posture-top__metrics--compact">
        <KpiCard
          label="Overall score"
          value={overviewLoading ? '…' : formatNumber(summary?.overallScore)}
          secondary={summary ? `${humanizeToken(summary.coverageStatus)} coverage` : 'No summary yet'}
          accent={scoreAccent(summary?.overallScore)}
        />
        <KpiCard
          label="Findings"
          value={overviewLoading ? '…' : formatNumber(findingsRollup?.totalFindings)}
          secondary={findingsRollup?.bySeverity ? `${formatNumber(highSeverityCount(findingsRollup.bySeverity))} high/critical hotspots` : 'No rollup yet'}
          accent={findingsAccent(findingsRollup)}
          onClick={() => openSummaryFindings()}
        />
        <KpiCard
          label="Overall Findings"
          value={orgOverviewLoading ? '…' : formatNumber(orgFindingsTotal)}
          secondary="Current organization-wide total, including site findings"
          accent={findingsAccent(orgOverview?.findings)}
          onClick={() => openSummaryFindings()}
        />
      </div>

      {overviewCoverageNotes.length ? (
        <div className="posture-callout posture-callout--warning">
          <div className="posture-callout__header">
            <ShieldIcon size={18} />
            <div>
              <h2>Coverage notes</h2>
              <p>Why this posture result may be partial or lower-confidence.</p>
            </div>
            <button
              type="button"
              className="posture-inline-link posture-inline-link--header"
              aria-expanded={overviewCoverageExpanded}
              onClick={() => setOverviewCoverageExpanded((value) => !value)}>
              {overviewCoverageExpanded ? 'Hide notes' : `Show ${formatNumber(overviewCoverageNotes.length)} note${overviewCoverageNotes.length === 1 ? '' : 's'}`}
            </button>
          </div>
          {overviewCoverageExpanded ? (
            <ul className="posture-bullet-list">
              {overviewCoverageNotes.map((note) => {
                const matchCount = countCoverageNoteFindingMatches(note, overviewLinkableFindings, siteNameById);
                const ruleLinkCount = coverageNoteFindingRuleIds(note, overviewLinkableFindings, siteNameById).length;
                return (
                  <li key={`${note.title}-${note.detail ?? note.items?.map((item) => item.key).join('|') ?? ''}`} className="posture-linked-bullet">
                    {note.items?.length ? (
                      <div>
                        <strong>{note.title}:</strong> {renderCoverageNoteDetail(note)}
                      </div>
                    ) : (
                      <span>
                        <strong>{note.title}:</strong> {renderCoverageNoteDetail(note)}
                      </span>
                    )}
                    {ruleLinkCount > 0 ? (
                      <button
                        type="button"
                        className="posture-inline-link"
                        onClick={() => focusFindingsForCoverageNote(note, overviewLinkableFindings)}>
                        {matchCount > 0
                          ? matchCount === 1
                            ? 'View rule'
                            : `View ${formatNumber(matchCount)} rules`
                          : ruleLinkCount === 1
                            ? 'View related rule'
                            : `View ${formatNumber(ruleLinkCount)} related rules`}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (!orgId) {
    return (
      <OrgModuleLayout title="Network Posture" description="Strengths, gaps, and actionable rule outcomes for the active organization.">
        <OrgModulePlaceholder intent="Select an organization to load posture data." />
      </OrgModuleLayout>
    );
  }

  const canStartManualRun = scopeType === 'site' && selectedSiteId.length > 0;
  const selectedRunSeverityCounts = summarizeSeverityCounts(selectedRun, selectedRunFindingRecords);
  const selectedRunDomainCounts = summarizeDomainCounts(selectedRunFindingRecords);
  const selectedRunReportStats = summarizeReportStats(selectedRun?.report);

  const resetFindingFilters = () => {
    setLinkedFindingRuleIds([]);
    setLinkedFindingLabel(null);
    setFindingsIncludeSiteItems(false);
    setFindingsSearch('');
    setFindingsHotspotsOnly(false);
    setFindingsHasEvidenceOnly(false);
    setFindingsHasRemediationOnly(false);
    setFindingsHasRecommendationOnly(false);
    setFindingsSeverityFilter('all');
    setFindingsDomainFilter('all');
    setFindingsSort('severity_desc');
  };

  const openSummaryFindings = (options?: { domain?: string; severity?: string; hotspotsOnly?: boolean }) => {
    setLinkedFindingRuleIds([]);
    setLinkedFindingLabel(null);
    setFindingsView('grouped');
    setFindingsIncludeSiteItems(scopeType === 'org');
    setFindingsSearch('');
    setFindingsHotspotsOnly(Boolean(options?.hotspotsOnly));
    setFindingsHasEvidenceOnly(false);
    setFindingsHasRemediationOnly(false);
    setFindingsHasRecommendationOnly(false);
    setFindingsSeverityFilter(options?.severity ?? 'all');
    setFindingsDomainFilter(options?.domain ?? 'all');
    setFindingsSort('severity_desc');
    setFindingsPage(1);
    setActiveTab('findings');
  };

  const focusFindingsForSummaryItem = (item: PostureSummaryItem) => {
    const ruleIds = summaryItemFindingRuleIds(item, overviewLinkableFindings);
    if (ruleIds.length === 0) {
      return;
    }
    setSelectedFinding(null);
    setLinkedFindingRuleIds(ruleIds);
    setLinkedFindingLabel(item.label.trim());
    setFindingsView('grouped');
    setFindingsIncludeSiteItems(scopeType === 'org');
    setFindingsSearch('');
    setFindingsHotspotsOnly(false);
    setFindingsHasEvidenceOnly(false);
    setFindingsHasRemediationOnly(false);
    setFindingsHasRecommendationOnly(false);
    setFindingsSeverityFilter('all');
    setFindingsDomainFilter('all');
    setFindingsSort('severity_desc');
    setFindingsPage(1);
    setActiveTab('findings');
    window.setTimeout(() => {
      if (typeof findingsSectionRef.current?.scrollIntoView === 'function') {
        findingsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const focusFindingsForCoverageNote = (
    note: PostureCoverageNote,
    findingsToMatch: PostureFindingRecord[] = overviewLinkableFindings
  ) => {
    const ruleIds = coverageNoteFindingRuleIds(note, findingsToMatch, siteNameById);
    if (ruleIds.length === 0) {
      return;
    }
    setSelectedFinding(null);
    setLinkedFindingRuleIds(ruleIds);
    setLinkedFindingLabel(note.title.trim());
    setFindingsView('grouped');
    setFindingsIncludeSiteItems(scopeType === 'org');
    setFindingsSearch('');
    setFindingsHotspotsOnly(false);
    setFindingsHasEvidenceOnly(false);
    setFindingsHasRemediationOnly(false);
    setFindingsHasRecommendationOnly(false);
    setFindingsSeverityFilter('all');
    setFindingsDomainFilter('all');
    setFindingsSort('severity_desc');
    setFindingsPage(1);
    setActiveTab('findings');
    window.setTimeout(() => {
      if (typeof findingsSectionRef.current?.scrollIntoView === 'function') {
        findingsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const resetRunFilters = () => {
    setRunsStatusFilter('all');
    setRunsTriggerFilter('all');
    setRunsSort('requested_desc');
  };

  const resetJobFilters = () => {
    setJobsStatusFilter('all');
    setJobsScopeFilter('all');
    setJobsSort('updated_desc');
  };

  const selectJob = (jobId: string | null) => {
    if (jobId && selectedRunId && !runs.some((run) => run.runId === selectedRunId && run.jobId === jobId)) {
      setSelectedRunId(null);
    }
    setSelectedJobId(jobId);
  };

  const openJobDetail = (jobId: string, options?: { closeRunDrawer?: boolean; select?: boolean }) => {
    if (options?.closeRunDrawer) {
      setSelectedRunId(null);
    }
    if (options?.select !== false) {
      selectJob(jobId);
    }
    setActiveJobId(jobId);
  };

  const openFindingPage = (record: PostureFindingRecord) => {
    navigate(
      buildPostureRouteHref(`/app/posture/findings/${encodeURIComponent(record.runId)}/${encodeURIComponent(findingRouteKey(record.finding))}`, {
        ...readPostureRouteState(),
        selectedRunId: record.runId
      })
    );
  };

  const recordRunFeedback = (runId: string, message: string, tone: 'success' | 'warning' = 'success') => {
    setRunFeedbackById((prev) => ({
      ...prev,
      [runId]: {
        message,
        tone,
        at: new Date().toISOString()
      }
    }));
  };

  const recordJobFeedback = (jobId: string, message: string, tone: 'success' | 'warning' = 'success') => {
    setJobFeedbackById((prev) => ({
      ...prev,
      [jobId]: {
        message,
        tone,
        at: new Date().toISOString()
      }
    }));
  };

  const selectedRunHiddenByFilters = Boolean(
    selectedRunId && scopedRuns.some((run) => run.runId === selectedRunId) && !filteredRuns.some((run) => run.runId === selectedRunId)
  );
  const selectedJobHiddenByFilters = Boolean(selectedJobId && jobs.some((job) => job.jobId === selectedJobId) && !filteredJobs.some((job) => job.jobId === selectedJobId));
  const runSectionNotice = runCancelError
    ? describePostureActionError('runs', runCancelError)
    : runSubmitSuccess
      ? `${runSubmitSuccess} It can take a short moment to appear in recent history.${selectedRunHiddenByFilters ? ' The run exists, but it is hidden by the current run filters. Clear filters to reveal it.' : ''}`
      : null;
  const runSectionNoticeTone = runCancelError ? 'error' : runSubmitSuccess ? 'success' : null;
  const jobSectionNotice = jobActionError
    ? describePostureActionError('jobs', jobActionError)
    : jobSaveSuccess
      ? `${jobSaveSuccess}${selectedJobHiddenByFilters ? ' The job exists, but it is hidden by the current job filters. Clear filters to reveal it.' : ''}`
      : null;
  const jobSectionNoticeTone = jobActionError ? 'error' : jobSaveSuccess ? (jobSaveTone === 'warning' ? 'error' : 'success') : null;
  const jobsHeaderStatus = jobsError ? describePostureCollectionError('jobs', jobsError.message) : `${formatNumber(filteredJobs.length)} of ${formatNumber(jobsResponse?.total ?? jobs.length)} jobs`;
  const runsHeaderStatus = runsError
    ? describePostureCollectionError('runs', runsError.message)
    : selectedJob
      ? `${formatNumber(filteredRuns.length)} of ${formatNumber(scopedRuns.length)} runs for ${selectedJob.name || shortRunId(selectedJob.jobId)}`
      : `${formatNumber(filteredRuns.length)} of ${formatNumber(runsResponse?.total ?? runs.length)} recent runs`;
  const runsHeaderDisplay = runsError
    ? runsHeaderStatus
    : selectedJob
      ? `${runsHeaderStatus}. Selected job narrows this run history.`
      : `${runsHeaderStatus}. Select a job to narrow the history.`;
  const jobFilterBar = (
    <PostureFilterBar
      summary={jobsHeaderStatus}
      controls={[
        <label key="status" className="posture-field">
          <span>Status</span>
          <select value={jobsStatusFilter} onChange={(event) => setJobsStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            {jobStatusOptions.map((option) => (
              <option key={option} value={option}>
                {humanizeToken(option)}
              </option>
            ))}
          </select>
        </label>,
        <label key="scope" className="posture-field">
          <span>Scope</span>
          <select value={jobsScopeFilter} onChange={(event) => setJobsScopeFilter(event.target.value)}>
            <option value="all">All scopes</option>
            <option value="org">Organization</option>
            <option value="site">Site</option>
          </select>
        </label>,
        <label key="sort" className="posture-field">
          <span>Sort</span>
          <select value={jobsSort} onChange={(event) => setJobsSort(event.target.value as typeof jobsSort)}>
            <option value="updated_desc">Most recent</option>
            <option value="name_asc">Name</option>
            <option value="scope_asc">Scope</option>
          </select>
        </label>
      ]}
    />
  );
  const runFilterBar = (
    <PostureFilterBar
      summary={runsHeaderStatus}
      controls={[
        <label key="status" className="posture-field">
          <span>Status</span>
          <select value={runsStatusFilter} onChange={(event) => setRunsStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            {runStatusOptions.map((option) => (
              <option key={option} value={option}>
                {humanizeToken(option)}
              </option>
            ))}
          </select>
        </label>,
        <label key="trigger" className="posture-field">
          <span>Trigger</span>
          <select value={runsTriggerFilter} onChange={(event) => setRunsTriggerFilter(event.target.value)}>
            <option value="all">All triggers</option>
            {runTriggerOptions.map((option) => (
              <option key={option} value={option}>
                {humanizeToken(option)}
              </option>
            ))}
          </select>
        </label>,
        <label key="sort" className="posture-field">
          <span>Sort</span>
          <select value={runsSort} onChange={(event) => setRunsSort(event.target.value as typeof runsSort)}>
            <option value="requested_desc">Most recent</option>
            <option value="score_desc">Highest score</option>
            <option value="status_asc">Status</option>
          </select>
        </label>
      ]}
    />
  );
  const findingsHeaderStatus = findingsError ? (
    <div className="list-workspace__status">Failed to load findings: {findingsError.message}</div>
  ) : (
    <>
      <PostureFilterBar
        summary={findingsSummary}
        controls={[
          <div key="field-row" className="posture-filter-row posture-filter-row--fields">
            <label className="posture-field posture-field--wide">
              <span>Search</span>
              <input
                className="posture-input"
                value={findingsSearch}
                onChange={(event) => setFindingsSearch(event.target.value)}
                placeholder="Title, detail, action, or rule ID"
              />
            </label>
            <label className="posture-field">
              <span>Severity</span>
              <select value={findingsSeverityFilter} onChange={(event) => setFindingsSeverityFilter(event.target.value)}>
                <option value="all">All severities</option>
                {findingSeverityOptions.map((option) => (
                  <option key={option} value={option}>
                    {humanizeToken(option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="posture-field">
              <span>Category</span>
              <select value={findingsDomainFilter} onChange={(event) => setFindingsDomainFilter(event.target.value)}>
                <option value="all">All categories</option>
                {findingDomainOptions.map((option) => (
                  <option key={option} value={option}>
                    {humanizeToken(option)}
                  </option>
                ))}
              </select>
            </label>
            <label className="posture-field">
              <span>Sort</span>
              <select value={findingsSort} onChange={(event) => setFindingsSort(event.target.value as typeof findingsSort)}>
                <option value="severity_desc">Severity</option>
                <option value="requested_desc">Most recent</option>
                <option value="domain_asc">Category</option>
              </select>
            </label>
          </div>,
          <div key="action-row" className="posture-filter-row posture-filter-row--actions">
            <button
              type="button"
              className={`ui-button ${findingsGroupedViewActive ? 'ui-button--primary' : 'ui-button--ghost'}`}
              onClick={() => setFindingsView((value) => (value === 'grouped' ? 'individual' : 'grouped'))}>
              {findingsGroupedViewActive ? 'Grouped by rule' : 'Individual findings'}
            </button>
            {scopeType === 'org' ? (
              <button
                type="button"
                className={`ui-button ${findingsIncludeSiteItems ? 'ui-button--primary' : 'ui-button--ghost'}`}
                onClick={() => setFindingsIncludeSiteItems((value) => !value)}>
                {findingsIncludeSiteItems ? 'Including site findings' : 'Include site findings'}
              </button>
            ) : null}
            <button
              type="button"
              className={`ui-button ${findingsHotspotsOnly ? 'ui-button--primary' : 'ui-button--ghost'}`}
              onClick={() => setFindingsHotspotsOnly((value) => !value)}>
              {findingsHotspotsOnly ? 'Showing high/critical' : 'High/Critical only'}
            </button>
            <button
              type="button"
              className={`ui-button ${findingsHasEvidenceOnly ? 'ui-button--primary' : 'ui-button--ghost'}`}
              onClick={() => setFindingsHasEvidenceOnly((value) => !value)}>
              {findingsHasEvidenceOnly ? 'Evidence required' : 'Has evidence'}
            </button>
            <button
              type="button"
              className={`ui-button ${findingsHasRemediationOnly ? 'ui-button--primary' : 'ui-button--ghost'}`}
              onClick={() => setFindingsHasRemediationOnly((value) => !value)}>
              {findingsHasRemediationOnly ? 'Remediation required' : 'Has remediation'}
            </button>
            <button
              type="button"
              className={`ui-button ${findingsHasRecommendationOnly ? 'ui-button--primary' : 'ui-button--ghost'}`}
              onClick={() => setFindingsHasRecommendationOnly((value) => !value)}>
              {findingsHasRecommendationOnly ? 'Recommendation required' : 'Has recommendation'}
            </button>
            {findingsFiltersActive ? (
              <button type="button" className="ui-button ui-button--ghost" onClick={resetFindingFilters}>
                Clear filters
              </button>
            ) : null}
          </div>
        ]}
      />

    </>
  );
  const findingsEmptyState = findingsFiltersActive ? (
    <p>No findings match the current filters. Clear filters or widen the scope.</p>
  ) : findingsError ? (
    <p>{describePostureCollectionError('findings', findingsError.message)}</p>
  ) : (
    <p>No posture findings are available for this scope yet.</p>
  );
  const runsEmptyState = runsFiltersActive ? (
    <p>No runs match the current filters. Clear filters to inspect the full run history.</p>
  ) : runsError ? (
    <p>{describePostureCollectionError('runs', runsError.message)}</p>
  ) : filteredRuns.length === 0 && scopeType === 'site' ? (
    <div className="posture-empty-state">
      <p>No posture runs exist for this site yet.</p>
      <p className="posture-empty-copy">Start a manual run now, or wait for a scheduled job to produce the first posture snapshot.</p>
      <button type="button" className="ui-button ui-button--primary" onClick={openManualRunDrawer} disabled={!canStartManualRun}>
        Run current site now
      </button>
    </div>
  ) : filteredRuns.length === 0 ? (
    <div className="posture-empty-state">
      <p>No posture runs exist for this scope yet.</p>
      <p className="posture-empty-copy">Organization scope shows completed site-level posture history after runs have been executed.</p>
    </div>
  ) : (
    <p>No posture runs are available for this scope yet.</p>
  );
  const jobsEmptyState = jobsFiltersActive ? (
    <p>No jobs match the current filters. Clear filters to inspect all configured jobs.</p>
  ) : jobsError ? (
    <p>{describePostureCollectionError('jobs', jobsError.message)}</p>
  ) : filteredJobs.length === 0 ? (
    <div className="posture-empty-state">
      <p>No posture jobs are configured yet.</p>
      <p className="posture-empty-copy">Create a posture job to enable recurring runs or save a reusable manual template.</p>
      <button type="button" className="ui-button ui-button--ghost" onClick={() => openCreateJobDrawer()}>
        Create posture job
      </button>
    </div>
  ) : (
    <p>No posture jobs configured yet.</p>
  );

  if (isFindingRoute) {
    return (
      <OrgModuleLayout
        title="Posture Finding"
        description="Detailed evidence, recommendation, and run context for a single posture finding."
        actions={
          <div className="posture-toolbar-actions">
            <button
              type="button"
              className="ui-button ui-button--ghost"
              onClick={() => navigate(buildPostureRouteHref('/app/posture', readPostureRouteState()))}>
              Back to posture
            </button>
            <button
              type="button"
              className="ui-button ui-button--ghost"
              onClick={() =>
                navigate(
                  buildPostureRouteHref('/app/posture', {
                    ...readPostureRouteState(),
                    selectedRunId: routeRunId
                  })
                )
              }>
              Return to run details
            </button>
            {routeFinding ? (
              <button
                type="button"
                className="ui-button ui-button--primary"
                onClick={() =>
                  navigate(
                    buildPostureRouteHref('/app/posture', {
                      ...readPostureRouteState(),
                      selectedRunId: routeFinding.runId
                    })
                  )
                }>
                Open run details
              </button>
            ) : null}
          </div>
        }>
        {selectedRunLoading ? (
          <OrgModulePlaceholder intent="Loading posture finding details…" />
        ) : selectedRunError ? (
          <OrgModulePlaceholder intent={`Failed to load the parent run: ${selectedRunError.message}`} />
        ) : routeFinding ? (
          <div className="posture-page">
            <PostureFindingDetail
              record={routeFinding}
              siteNameById={siteNameById}
              orgDisplayName={orgDisplayName}
              deviceSummaryById={deviceSummaryById}
            />
          </div>
        ) : (
          <OrgModulePlaceholder intent="This posture finding could not be resolved from the selected run." />
        )}
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout
      title="Network Posture"
      description="Current network strengths, weaknesses, and actionable rule outcomes across organization and site scopes."
      topSlot={topSlot}
      actions={
        activeTab === 'setup' ? (
          <div className="posture-toolbar-actions">
            <button type="button" className="ui-button ui-button--ghost" onClick={() => openCreateJobDrawer()}>
              Create posture job
            </button>
            <button type="button" className="ui-button ui-button--primary" onClick={openManualRunDrawer} disabled={!canStartManualRun}>
              Run posture now
            </button>
          </div>
        ) : null
      }>
      {showGlobalBackendMissing ? (
        <OrgModulePlaceholder intent="Posture APIs are not available in this environment yet." />
      ) : !scopeReady ? (
        <OrgModulePlaceholder intent="Pick a site to load site-level posture and enable manual run controls." />
      ) : (
        <div className="posture-page">
          {showSiteScopeBackendMissing ? (
            <div className="list-workspace__status">Site-level posture APIs are not available in this environment yet.</div>
          ) : overviewError ? (
            <div className="list-workspace__status">Failed to load posture overview: {overviewError.message}</div>
          ) : null}
          {sitesError ? <div className="list-workspace__status">Failed to load sites: {sitesError.message}</div> : null}
          <Tabs items={postureTabs} activeId={activeTab} onChange={(id) => setActiveTab(id as PosturePageTab)} />

          {activeTab === 'summary' ? (
            <>
              <section className="posture-section">
                <div className="posture-section__header">
                  <div>
                    <h2>Scope summary</h2>
                    <p>Use the summary strip above, then review the actions, strengths, and domain breakdown for this scope.</p>
                  </div>
                </div>
              </section>

              <section className="posture-highlights">
                <div className="posture-callout">
                  <div className="posture-callout__header">
                    <ShieldIcon size={18} />
                    <div>
                      <h2>Priority actions</h2>
                      <p>What to fix first.</p>
                    </div>
                  </div>
                  {priorityActionItems.length > 0 ? (
                    <ul className="posture-bullet-list">
                      {priorityActionItems.map((item, index) => {
                        const matchCount = countSummaryItemFindingMatches(item, overviewLinkableFindings);
                        const ruleLinkCount = summaryItemFindingRuleIds(item, overviewLinkableFindings).length;
                        return (
                          <li key={`${item.label}-${index}`} className="posture-linked-bullet">
                            <span>{item.label}</span>
                            {ruleLinkCount > 0 ? (
                              <button type="button" className="posture-inline-link" onClick={() => focusFindingsForSummaryItem(item)}>
                                {matchCount > 0
                                  ? matchCount === 1
                                    ? 'View rule'
                                    : `View ${formatNumber(matchCount)} rules`
                                  : ruleLinkCount === 1
                                    ? 'View related rule'
                                    : `View ${formatNumber(ruleLinkCount)} related rules`}
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="posture-empty-copy">No prioritized actions were returned for the latest run.</p>
                  )}
                </div>

                <div className="posture-callout posture-callout--success">
                  <div className="posture-callout__header">
                    <ActivityIcon size={18} />
                    <div>
                      <h2>Strengths</h2>
                      <p>What is holding up well.</p>
                    </div>
                  </div>
                  {strengthItems.length > 0 ? (
                    <ul className="posture-bullet-list">
                      {strengthItems.map((item, index) => {
                        const matchCount = countSummaryItemFindingMatches(item, overviewLinkableFindings);
                        const ruleLinkCount = summaryItemFindingRuleIds(item, overviewLinkableFindings).length;
                        return (
                          <li key={`${item.label}-${index}`} className="posture-linked-bullet">
                            <span>{item.label}</span>
                            {ruleLinkCount > 0 ? (
                              <button type="button" className="posture-inline-link" onClick={() => focusFindingsForSummaryItem(item)}>
                                {matchCount > 0
                                  ? matchCount === 1
                                    ? 'View rule'
                                    : `View ${formatNumber(matchCount)} rules`
                                  : ruleLinkCount === 1
                                    ? 'View related rule'
                                    : `View ${formatNumber(ruleLinkCount)} related rules`}
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="posture-empty-copy">No strengths were persisted for the latest run.</p>
                  )}
                </div>
              </section>

              <section className="posture-section">
                <div className="posture-section__header">
                  <div>
                    <h2>Domain scores</h2>
                    <p>Detailed breakdown by rule-pack area.</p>
                  </div>
                </div>
                {domainScores.length > 0 ? (
                  <div className="kpi-grid posture-domain-grid">
                    {domainScores.map(([domain, score]) => (
                      <KpiCard
                        key={domain}
                        label={humanizeToken(domain)}
                        value={formatNumber(score)}
                        secondary={findingsByDomainLabel(findingsRollup?.byDomain?.[domain])}
                        accent={scoreAccent(score)}
                        onClick={() => openSummaryFindings({ domain: normalizeToken(domain) || 'all' })}
                      />
                    ))}
                  </div>
                ) : (
                  <OrgModulePlaceholder intent="No domain scores were persisted for this scope yet." />
                )}
              </section>
            </>
          ) : activeTab === 'findings' ? (
            <>
          <div ref={findingsSectionRef}>
            {linkedFindingRuleIds.length > 0 ? (
              <div className="posture-linked-filter">
                <span>Filtered to rules for {linkedFindingLabel ?? 'selected summary item'}.</span>
                <button type="button" className="posture-inline-link" onClick={resetFindingFilters}>
                  Clear filter
                </button>
              </div>
            ) : null}
          </div>
          {findingsGroupedViewActive ? (
            <ListWorkspace
              rows={pagedFindingGroups}
              columns={findingGroupColumns}
              rowId={(row) => row.key}
              totalCount={findingsTotal}
              paginationMeta={findingsPaginationMeta}
              page={findingsPage}
              pageSize={findingsPageSize}
              pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
              onPageChange={setFindingsPage}
              onPageSizeChange={(size) => {
                setFindingsPageSize(size);
                setFindingsPage(1);
              }}
              loading={findingsLoading}
              selectionDisabled
              collapseToolbarWhenEmpty
              headerStatus={findingsHeaderStatus}
              emptyState={findingsEmptyState}
            />
          ) : (
            <ListWorkspace
              rows={pagedFindings}
              columns={findingsColumns}
              rowId={(row, index) => `${row.runId}-${row.finding.ruleId}-${row.finding.scopeId}-${index}`}
              totalCount={findingsTotal}
              page={findingsPage}
              pageSize={findingsPageSize}
              paginationMeta={findingsPaginationMeta}
              pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
              onPageChange={setFindingsPage}
              onPageSizeChange={(size) => {
                setFindingsPageSize(size);
                setFindingsPage(1);
              }}
              loading={findingsLoading}
              selectionDisabled
              collapseToolbarWhenEmpty
              headerStatus={findingsHeaderStatus}
              emptyState={findingsEmptyState}
              onRowClick={(row) => setSelectedFinding(row)}
            />
          )}
            </>
          ) : (
            <>
              <div className="discovery-stack">
                <section className="discovery-stack__panel discovery-stack__panel--jobs">
                  {jobSectionNoticeTone ? <div className={`posture-notice posture-notice--${jobSectionNoticeTone}`}>{jobSectionNotice}</div> : null}
                  <ListWorkspace
                    rows={pagedJobs}
                    columns={jobColumns}
                    rowId={(row) => row.jobId}
                    totalCount={jobsResponse?.total ?? jobs.length}
                    page={jobsPage}
                    pageSize={jobsPageSize}
                    pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
                    onPageChange={setJobsPage}
                    onPageSizeChange={(size) => {
                      setJobsPageSize(size);
                      setJobsPage(1);
                    }}
                    loading={jobsLoading}
                    selectionDisabled
                    headerLabel="Jobs"
                    headerStatus={jobsHeaderStatus}
                    filterBar={jobFilterBar}
                    filterActive={jobsFiltersActive}
                    onFilterReset={resetJobFilters}
                    emptyState={jobsEmptyState}
                    highlightRowId={selectedJobId}
                    onRefresh={() => setRefreshNonce((value) => value + 1)}
                    onAdd={() => openCreateJobDrawer()}
                    onRowClick={(row) => selectJob(row.jobId)}
                    toolbarActions={
                      selectedJobId ? (
                        <button type="button" className="ui-button ui-button--ghost" onClick={() => selectJob(null)}>
                          Clear selection
                        </button>
                      ) : null
                    }
                    rowActions={(row) => (
                      <div className="posture-row-actions">
                        <button
                          type="button"
                          className="ui-button ui-button--ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            openJobDetail(row.jobId, { select: true });
                          }}>
                          Details
                        </button>
                        <button
                          type="button"
                          className="ui-button ui-button--ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleTriggerJob(row);
                          }}
                          disabled={row.status === 'paused' || jobTriggerBusyId === row.jobId}>
                          {jobTriggerBusyId === row.jobId ? 'Starting…' : 'Run now'}
                        </button>
                        <button
                          type="button"
                          className="ui-button ui-button--ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleToggleJobStatus(row);
                          }}
                          disabled={jobToggleBusyId === row.jobId}>
                          {jobToggleBusyId === row.jobId ? 'Saving…' : row.status === 'paused' ? 'Activate' : 'Pause'}
                        </button>
                        <button
                          type="button"
                          className="ui-button ui-button--ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeleteJob(row);
                          }}
                          disabled={jobDeleteBusyId === row.jobId}>
                          {jobDeleteBusyId === row.jobId ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    )}
                  />
                </section>

                <section className="discovery-stack__panel discovery-stack__panel--runs">
                  {runSectionNoticeTone ? <div className={`posture-notice posture-notice--${runSectionNoticeTone}`}>{runSectionNotice}</div> : null}
                  <ListWorkspace
                    rows={pagedRuns}
                    columns={runColumns}
                    rowId={(row) => row.runId}
                    totalCount={runsResponse?.total ?? runs.length}
                    page={runsPage}
                    pageSize={runsPageSize}
                    pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
                    onPageChange={setRunsPage}
                    onPageSizeChange={(size) => {
                      setRunsPageSize(size);
                      setRunsPage(1);
                    }}
                    loading={runsLoading}
                    selectionDisabled
                    headerLabel="Runs"
                    headerStatus={runsHeaderDisplay}
                    filterBar={runFilterBar}
                    filterActive={runsFiltersActive}
                    onFilterReset={resetRunFilters}
                    emptyState={runsEmptyState}
                    highlightRowId={selectedRunId}
                    onRefresh={() => setRefreshNonce((value) => value + 1)}
                    onAdd={canStartManualRun ? openManualRunDrawer : undefined}
                    onRowClick={(row) => {
                      setSelectedRunId(row.runId);
                      setReportActionStatus(null);
                    }}
                    rowActions={(row) => (
                      <div className="posture-row-actions">
                        <button
                          type="button"
                          className="ui-button ui-button--ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedRunId(row.runId);
                            setReportActionStatus(null);
                          }}>
                          Details
                        </button>
                        {isRunCancelable(row.status) ? (
                          <button
                            type="button"
                            className="ui-button ui-button--ghost"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleCancelRun(row.runId);
                            }}
                            disabled={runCancelBusyId === row.runId}>
                            {runCancelBusyId === row.runId ? 'Cancelling…' : 'Cancel'}
                          </button>
                        ) : null}
                      </div>
                    )}
                  />
                </section>
              </div>
            </>
          )}
        </div>
      )}

      <Drawer open={runDrawerOpen} title="Run posture now" subtitle={canStartManualRun ? currentScopeLabel : 'Site scope required'} onClose={() => setRunDrawerOpen(false)}>
        <div className="posture-drawer">
          <div className="form-field">
            <span className="form-field__label">Target site</span>
            <input className="input" value={canStartManualRun ? currentScopeLabel : 'Select a site first'} disabled />
            <span className="form-field__hint">Manual durable runs are supported for site scope only.</span>
          </div>

          <div className="form-field">
            <span className="form-field__label">Enabled rule packs</span>
            <RulePackSelector
              value={runForm.enabledRulePacks}
              onChange={(enabledRulePacks) => {
                setRunRulePacksDirty(true);
                setRunForm({ enabledRulePacks });
              }}
              options={selectableRulePacks}
              loading={rulePackCatalogLoading}
              errorMessage={rulePackCatalogError?.message ?? null}
            />
          </div>

          {runSubmitError ? <div className="form-error">{runSubmitError}</div> : null}

          <div className="form-actions">
            <button type="button" className="ui-button ui-button--ghost" onClick={() => setRunDrawerOpen(false)} disabled={runSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="ui-button ui-button--primary"
              onClick={() => void handleStartManualRun()}
              disabled={runSubmitting || !canStartManualRun || !rulePackDefaultsReady}
            >
              {runSubmitting ? 'Starting…' : 'Start run'}
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer open={jobDrawerOpen} title="Create posture job" subtitle="Create a reusable recurring or manual posture job template." onClose={() => setJobDrawerOpen(false)}>
        <div className="posture-drawer">
          <label className="form-field">
            <span className="form-field__label">Job name</span>
            <input className="input" value={jobForm.name} onChange={(event) => setJobForm((prev) => ({ ...prev, name: event.target.value }))} />
          </label>

          <label className="form-field">
            <span className="form-field__label">Scope</span>
            <select className="select" value={jobForm.scopeType} onChange={(event) => setJobForm((prev) => ({ ...prev, scopeType: event.target.value === 'site' ? 'site' : 'org' }))}>
              <option value="org">Organization</option>
              <option value="site">Site</option>
            </select>
          </label>

          {jobForm.scopeType === 'site' ? (
            <label className="form-field">
              <span className="form-field__label">Site</span>
              <select className="select" value={jobForm.siteId} onChange={(event) => setJobForm((prev) => ({ ...prev, siteId: event.target.value }))}>
                <option value="">{sites.length === 0 ? 'No sites available' : 'Select site…'}</option>
                {sites.map((site) => (
                  <option key={site.id ?? site.name} value={site.id}>
                    {site.name || site.id}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="form-field">
            <span className="form-field__label">Schedule mode</span>
            <select
              className="select"
              value={jobForm.scheduleMode}
              onChange={(event) => setJobForm((prev) => ({ ...prev, scheduleMode: event.target.value === 'manual' ? 'manual' : 'recurring' }))}
            >
              <option value="recurring">Recurring</option>
              <option value="manual">Manual template</option>
            </select>
          </label>

          {jobForm.scheduleMode === 'recurring' ? (
            <label className="form-field">
              <span className="form-field__label">Cron</span>
              <input className="input" value={jobForm.cron} onChange={(event) => setJobForm((prev) => ({ ...prev, cron: event.target.value }))} />
              <span className="form-field__hint">Example: `0 2 * * *` for daily 2 AM posture execution.</span>
            </label>
          ) : null}

          <label className="form-field">
            <span className="form-field__label">Initial status</span>
            <select className="select" value={jobForm.status} onChange={(event) => setJobForm((prev) => ({ ...prev, status: event.target.value === 'paused' ? 'paused' : 'active' }))}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </label>

          <div className="form-field">
            <span className="form-field__label">Enabled rule packs</span>
            <RulePackSelector
              value={jobForm.enabledRulePacks}
              onChange={(enabledRulePacks) => {
                setJobRulePacksDirty(true);
                setJobForm((prev) => ({ ...prev, enabledRulePacks }));
              }}
              options={selectableRulePacks}
              loading={rulePackCatalogLoading}
              errorMessage={rulePackCatalogError?.message ?? null}
            />
          </div>

          {jobSaveError ? <div className="form-error">{jobSaveError}</div> : null}

          <div className="form-actions">
            <button type="button" className="ui-button ui-button--ghost" onClick={() => setJobDrawerOpen(false)} disabled={jobSaving}>
              Cancel
            </button>
            <button type="button" className="ui-button ui-button--primary" onClick={() => void handleCreateJob()} disabled={jobSaving || !rulePackDefaultsReady}>
              {jobSaving ? 'Creating…' : 'Create job'}
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer
        open={activeFinding != null && !isFindingRoute}
        title={activeFinding ? findingDisplayTitle(activeFinding.finding) : 'Finding'}
        subtitle={
          activeFinding
            ? `${resolveScopeLabel(activeFinding.finding.scopeType, activeFinding.finding.scopeId, siteNameById, orgDisplayName, deviceSummaryById)} • ${findingCategoryLabel(activeFinding.finding)}`
            : undefined
        }
        onClose={() => setSelectedFinding(null)}
        width="wide"
        actions={
          activeFinding ? (
            <>
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() => openFindingPage(activeFinding)}>
                Open full page
              </button>
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() => {
                  setSelectedFinding(null);
                  setSelectedRunId(activeFinding.runId);
                }}>
                Open run details
              </button>
            </>
          ) : undefined
        }>
        {activeFinding ? (
          <PostureFindingDetail
            record={activeFinding}
            siteNameById={siteNameById}
            orgDisplayName={orgDisplayName}
            deviceSummaryById={deviceSummaryById}
          />
        ) : (
          <p>Select a finding to inspect its evidence and recommendation.</p>
        )}
      </Drawer>

      <Drawer
        open={activeJobId != null}
        title={activeJob?.name?.trim() || (activeJobId ? `Posture job ${shortRunId(activeJobId)}` : 'Posture job')}
        subtitle={activeJob ? `${resolveJobScopeLabel(activeJob, siteNameById, orgDisplayName)} • ${humanizeToken(activeJob.status)}` : undefined}
        onClose={() => setActiveJobId(null)}
        actions={
          activeJob ? (
            <>
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() => void handleTriggerJob(activeJob)}
                disabled={activeJob.status === 'paused' || jobTriggerBusyId === activeJob.jobId}>
                {jobTriggerBusyId === activeJob.jobId ? 'Starting…' : 'Run now'}
              </button>
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() => openCreateJobDrawer(activeJob)}
                disabled={jobSaving}>
                Duplicate
              </button>
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() => void handleToggleJobStatus(activeJob)}
                disabled={jobToggleBusyId === activeJob.jobId}>
                {jobToggleBusyId === activeJob.jobId ? 'Saving…' : activeJob.status === 'paused' ? 'Activate' : 'Pause'}
              </button>
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() => void handleDeleteJob(activeJob)}
                disabled={jobDeleteBusyId === activeJob.jobId}>
                {jobDeleteBusyId === activeJob.jobId ? 'Deleting…' : 'Delete'}
              </button>
            </>
          ) : undefined
        }>
        {activeJob ? (
          <div className="posture-drawer">
            {selectedJobFeedback ? (
              <div className={`posture-notice posture-notice--${selectedJobFeedback.tone === 'warning' ? 'error' : 'success'}`}>
                {selectedJobFeedback.message} {formatRelativeTime(selectedJobFeedback.at)}
              </div>
            ) : null}
            <section className="record-summary">
              <h3 className="record-summary__title">Job summary</h3>
          <div className="record-summary__grid">
                <div>
                  <span>Status</span>
                  <strong>{humanizeToken(activeJob.status)}</strong>
                </div>
                <div>
                  <span>Scope</span>
                  <strong>{resolveJobScopeLabel(activeJob, siteNameById, orgDisplayName)}</strong>
                </div>
                <div>
                  <span>Schedule mode</span>
                  <strong>{humanizeToken(activeJob.schedule?.mode || 'manual')}</strong>
                </div>
                <div>
                  <span>Cron</span>
                  <strong className="record-summary__multiline">{activeJob.schedule?.cron || '—'}</strong>
                </div>
                <div>
                  <span>Scheduled task</span>
                  <strong className="record-summary__multiline" title={activeJob.scheduledTaskId || undefined}>
                    {formatOpaqueIdentifier(activeJob.scheduledTaskId) || '—'}
                  </strong>
                </div>
                <div>
                  <span>Pack selection</span>
                  <strong>{activeJob.enabledRulePacks?.length ? 'Explicit' : 'Default'}</strong>
                </div>
                <div>
                  <span>Rule packs</span>
                  <strong>{formatNumber(effectiveRulePacks(activeJob.enabledRulePacks, defaultEnabledRulePacks).length)}</strong>
                </div>
                <div>
                  <span>Created</span>
                  <strong>{formatDateTime(activeJob.createdAt)}</strong>
                </div>
                <div>
                  <span>Updated</span>
                  <strong>{formatDateTime(activeJob.updatedAt)}</strong>
                </div>
              </div>
            </section>

            <section className="posture-detail-grid">
              <div className="posture-detail-section">
                <h3>Effective rule packs</h3>
                {effectiveRulePacks(activeJob.enabledRulePacks, defaultEnabledRulePacks).length ? (
                  <>
                    <p className="posture-empty-copy">
                      {activeJob.enabledRulePacks?.length
                        ? 'This job stores an explicit pack selection.'
                        : `This job inherits the default pack set: ${defaultRulePackDescription(defaultEnabledRulePacks, rulePackLabelById)}.`}
                    </p>
                    <div className="posture-chip-group">
                      {effectiveRulePacks(activeJob.enabledRulePacks, defaultEnabledRulePacks).map((rulePack) => (
                        <span key={rulePack} className="posture-chip posture-chip--neutral">
                          {resolveRulePackLabel(rulePack, rulePackLabelById)}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="posture-empty-copy">No rule packs were persisted for this job.</p>
                )}
              </div>

              <div className="posture-detail-section">
                <h3>Stored pack override</h3>
                {activeJob.enabledRulePacks?.length ? (
                  <div className="posture-chip-group">
                    {activeJob.enabledRulePacks.map((rulePack) => (
                      <span key={rulePack} className="posture-chip posture-chip--neutral">
                        {resolveRulePackLabel(rulePack, rulePackLabelById)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="posture-empty-copy">No explicit override is stored; the service default applies.</p>
                )}
              </div>
            </section>

            <section className="posture-detail-grid">
              <div className="posture-detail-section">
                <h3>Scope selector</h3>
                {activeJob.scopeSelector && Object.keys(activeJob.scopeSelector).length ? (
                  <div className="posture-chip-group">
                    {describeScopeSelector(activeJob.scopeSelector, siteNameById, orgDisplayName).map((item) => (
                      <span key={item} className="posture-chip posture-chip--neutral">
                        {item}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="posture-empty-copy">No extra scope selector is stored for this job.</p>
                )}
              </div>

              <div className="posture-detail-section">
                <h3>Disabled rules</h3>
                {activeJob.disabledRuleIds?.length ? (
                  <div className="posture-chip-group">
                    {activeJob.disabledRuleIds.map((ruleId) => (
                      <span key={ruleId} className="posture-chip posture-chip--warning" title={ruleId}>
                        {humanizeRuleReference(ruleId)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="posture-empty-copy">No explicit disabled rules were stored.</p>
                )}
              </div>

              <div className="posture-detail-section">
                <h3>Report options</h3>
                {activeJob.reportOptions && Object.keys(activeJob.reportOptions).length ? (
                  <pre className="posture-report">{JSON.stringify(activeJob.reportOptions, null, 2)}</pre>
                ) : (
                  <p className="posture-empty-copy">No custom report options were stored.</p>
                )}
              </div>
            </section>
          </div>
        ) : (
          <p>Select a job to inspect its configuration.</p>
        )}
      </Drawer>

      <Drawer
        open={selectedRunId != null}
        title={selectedRun ? resolveRunDisplayName(selectedRun, siteNameById, orgDisplayName) : selectedRunId ? `Posture run ${shortRunId(selectedRunId)}` : 'Posture run'}
        subtitle={selectedRun ? `${resolveScopeLabel(selectedRun.scopeType, selectedRun.scopeId, siteNameById, orgDisplayName)} • ${humanizeToken(selectedRun.triggerType)}` : undefined}
        onClose={() => {
          setSelectedRunId(null);
          setReportActionStatus(null);
        }}
        width="wide"
        actions={
          selectedRun ? (
            <>
              {selectedRun.report?.content ? (
                <>
                  <button type="button" className="ui-button ui-button--ghost" onClick={() => void handleCopyReport(selectedRun)}>
                    <CopyIcon size={14} /> Copy report
                  </button>
                  <button type="button" className="ui-button ui-button--ghost" onClick={() => handleDownloadReport(selectedRun)}>
                    <DownloadIcon size={14} /> Download report
                  </button>
                </>
              ) : null}
              {isRunCancelable(selectedRun.status) ? (
                <button
                  type="button"
                  className="ui-button ui-button--ghost"
                  onClick={() => void handleCancelRun(selectedRun.runId)}
                  disabled={runCancelBusyId === selectedRun.runId}>
                  {runCancelBusyId === selectedRun.runId ? 'Cancelling…' : 'Cancel run'}
                </button>
              ) : null}
            </>
          ) : undefined
        }>
        {selectedRunLoading ? (
          <p>Loading run details…</p>
        ) : selectedRunError ? (
          <p>Failed to load run details: {selectedRunError.message}</p>
        ) : selectedRun ? (
          <div className="posture-drawer">
            {reportActionStatus ? <div className="posture-notice posture-notice--success">{reportActionStatus}</div> : null}
            {selectedRunFeedback ? (
              <div className={`posture-notice posture-notice--${selectedRunFeedback.tone === 'warning' ? 'error' : 'success'}`}>
                {selectedRunFeedback.message} {formatRelativeTime(selectedRunFeedback.at)}
              </div>
            ) : null}
            <section className="record-summary">
              <h3 className="record-summary__title">Run summary</h3>
              <div className="record-summary__grid">
                <div>
                  <span>Status</span>
                  <strong>{humanizeToken(selectedRun.status)}</strong>
                </div>
                <div>
                  <span>Coverage</span>
                  <strong>{humanizeToken(selectedRun.coverageStatus)}</strong>
                </div>
                <div>
                  <span>Pack selection</span>
                  <strong>{selectedRun.enabledRulePacks?.length ? 'Persisted on run' : 'Unavailable on historical run'}</strong>
                </div>
                <div>
                  <span>Overall score</span>
                  <strong>{formatNumber(selectedRun.summary?.overallScore)}</strong>
                </div>
                <div>
                  <span>Rule packs</span>
                  <strong>{selectedRunPersistedRulePacks.length ? formatNumber(selectedRunPersistedRulePacks.length) : '—'}</strong>
                </div>
                <div>
                  <span>Findings</span>
                  <strong>{formatNumber(selectedRunFindingRecords.length || totalFindingCount(selectedRun.summary?.findingCounts))}</strong>
                </div>
                <div>
                  <span>Requested</span>
                  <strong>{formatDateTime(selectedRun.requestedAt)}</strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>{formatDuration(selectedRun.startedAt ?? selectedRun.requestedAt, selectedRun.finishedAt)}</strong>
                </div>
                <div>
                  <span>Parent run</span>
                  {selectedRun.parentRunId ? (
                    <strong className="record-summary__multiline">
                      <button type="button" className="posture-link-button" onClick={() => setSelectedRunId(selectedRun.parentRunId!)}>
                        {resolveRunLabel(selectedRun.parentRunId, runs, siteNameById, orgDisplayName)}
                      </button>
                    </strong>
                  ) : (
                    <strong className="record-summary__multiline">—</strong>
                  )}
                </div>
                <div>
                  <span>Source job</span>
                  {selectedRun.jobId ? (
                    <strong className="record-summary__multiline">
                      <button
                        type="button"
                        className="posture-chip posture-chip--neutral"
                        onClick={() => openJobDetail(selectedRun.jobId!, { closeRunDrawer: true })}>
                        {selectedRunJob?.name?.trim() || resolveRunJobLabel(selectedRun.jobId, jobs)}
                      </button>
                    </strong>
                  ) : (
                    <strong>Manual / direct trigger</strong>
                  )}
                </div>
                <div>
                  <span>Config fingerprint</span>
                  <strong className="record-summary__multiline" title={selectedRun.configFingerprint || undefined}>
                    {formatOpaqueIdentifier(selectedRun.configFingerprint, 12) || '—'}
                  </strong>
                </div>
              </div>
            </section>

            {selectedRun.error?.message ? (
              <section className="posture-detail-section">
                <h3>Execution error</h3>
                <p>{selectedRun.error.message}</p>
              </section>
            ) : null}

            {selectedRunLifecycle.length ? (
              <section className="posture-detail-section">
                <h3>Lifecycle</h3>
                <ul className="posture-bullet-list">
                  {selectedRunLifecycle.map((item) => (
                    <li key={item}>
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {selectedRunCoverageNotes.length ? (
              <section className="posture-detail-section">
                <h3>Coverage notes</h3>
                <ul className="posture-bullet-list">
                  {selectedRunCoverageNotes.map((note) => {
                    const matchCount = countCoverageNoteFindingMatches(note, selectedRunFindingRecords, siteNameById);
                    const ruleLinkCount = coverageNoteFindingRuleIds(note, selectedRunFindingRecords, siteNameById).length;
                    return (
                      <li
                        key={`${note.title}-${note.detail ?? note.items?.map((item) => item.key).join('|') ?? ''}`}
                        className={ruleLinkCount > 0 ? 'posture-linked-bullet' : undefined}>
                        {note.items?.length ? (
                          <div>
                            <strong>{note.title}:</strong> {renderCoverageNoteDetail(note)}
                          </div>
                        ) : (
                          <span>
                            <strong>{note.title}:</strong> {renderCoverageNoteDetail(note)}
                          </span>
                        )}
                        {ruleLinkCount > 0 ? (
                          <button type="button" className="posture-inline-link" onClick={() => focusFindingsForCoverageNote(note, selectedRunFindingRecords)}>
                            {matchCount > 0
                              ? matchCount === 1
                                ? 'View rule'
                                : `View ${formatNumber(matchCount)} rules`
                              : ruleLinkCount === 1
                                ? 'View related rule'
                                : `View ${formatNumber(ruleLinkCount)} related rules`}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {selectedRunChildRunsError ? (
              <section className="posture-detail-section">
                <h3>Related site runs</h3>
                <p className="posture-empty-copy">Failed to load child site runs: {selectedRunChildRunsError.message}</p>
              </section>
            ) : null}

            {selectedRunAggregatesChildFindings ? (
              <section className="posture-detail-section">
                <h3>Rolled-up site findings</h3>
                <p className="posture-empty-copy">
                  This organization run stores detailed findings on its child site runs. Showing {formatNumber(selectedRunFindingRecords.length)} findings from{' '}
                  {formatNumber(selectedRunChildRunCount)} child run{selectedRunChildRunCount === 1 ? '' : 's'}.
                </p>
              </section>
            ) : null}

            <section className="posture-detail-section">
              <h3>Effective rule packs</h3>
              {selectedRunPersistedRulePacks.length ? (
                <>
                  <p className="posture-empty-copy">These packs were persisted with the run payload.</p>
                  <div className="posture-chip-group">
                    {selectedRunPersistedRulePacks.map((rulePack) => (
                      <span key={rulePack} className="posture-chip posture-chip--neutral">
                        {resolveRulePackLabel(rulePack, rulePackLabelById)}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <p className="posture-empty-copy">
                  This historical run did not persist its rule-pack list, so the exact pack set used at execution time is unavailable.
                </p>
              )}
            </section>

            {!selectedRunPersistedRulePacks.length && selectedRunCurrentJobRulePacks.length ? (
              <section className="posture-detail-section">
                <h3>Current job configuration</h3>
                <p className="posture-empty-copy">
                  The source job currently enables these packs. This can help with triage, but it may differ from the configuration that was active when the run executed.
                </p>
                <div className="posture-chip-group">
                  {selectedRunCurrentJobRulePacks.map((rulePack) => (
                    <span key={rulePack} className="posture-chip posture-chip--neutral">
                      {resolveRulePackLabel(rulePack, rulePackLabelById)}
                    </span>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="posture-detail-grid">
              <div className="posture-detail-section">
                <h3>Severity mix</h3>
                {selectedRunSeverityCounts.length ? (
                  <div className="posture-chip-group">
                    {selectedRunSeverityCounts.map(([severity, count]) => (
                      <span key={severity} className={`posture-chip ${severityToneClass(severity)}`}>
                        {humanizeToken(severity)} {formatNumber(count)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="posture-empty-copy">No severity totals recorded.</p>
                )}
              </div>

              <div className="posture-detail-section">
                <h3>Domains touched</h3>
                {selectedRunDomainCounts.length ? (
                  <div className="posture-chip-group">
                    {selectedRunDomainCounts.map(([domain, count]) => (
                      <span key={domain} className="posture-chip posture-chip--neutral">
                        {humanizeToken(domain)} {formatNumber(count)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="posture-empty-copy">No domain totals recorded.</p>
                )}
              </div>
            </section>

            <section className="posture-detail-grid">
              <div className="posture-detail-section">
                <h3>Priority actions</h3>
                {(selectedRun.summary?.priorityActionItems?.length || selectedRun.summary?.priorityActions?.length) ? (
                  <ul className="posture-bullet-list">
                    {(selectedRun.summary?.priorityActionItems?.length
                      ? selectedRun.summary.priorityActionItems.map((item) => item.label)
                      : selectedRun.summary?.priorityActions ?? []
                    ).map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="posture-empty-copy">No priority actions recorded.</p>
                )}
              </div>

              <div className="posture-detail-section">
                <h3>Strengths</h3>
                {(selectedRun.summary?.strengthItems?.length || selectedRun.summary?.strengths?.length) ? (
                  <ul className="posture-bullet-list">
                    {(selectedRun.summary?.strengthItems?.length
                      ? selectedRun.summary.strengthItems.map((item) => item.label)
                      : selectedRun.summary?.strengths ?? []
                    ).map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="posture-empty-copy">No strengths recorded.</p>
                )}
              </div>
            </section>

            <section className="posture-detail-section">
              <h3>Findings</h3>
              {selectedRun.scopeType === 'org' && selectedRunChildRunsLoading && !selectedRunFindingRecords.length ? (
                <p className="posture-empty-copy">Loading child site-run findings…</p>
              ) : selectedRunFindingRecords.length ? (
                <div className="posture-detail-findings">
                  {selectedRunFindingRecords.map((record, index) => (
                    <article
                      key={`${findingRowId(record)}-${index}`}
                      className="posture-finding-card"
                      onClick={() => {
                        setSelectedRunId(null);
                        setSelectedFinding(record);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedRunId(null);
                          setSelectedFinding(record);
                        }
                      }}>
                      <div className="posture-finding-card__header">
                        <span className={severityBadgeClass(record.finding.severity)}>{humanizeToken(record.finding.severity)}</span>
                        <span className="ui-badge ui-badge--ghost">{findingCategoryLabel(record.finding)}</span>
                        {normalizeToken(record.finding.scopeType) === 'device' && record.finding.scopeId.trim() ? (
                          <a
                            href={buildDeviceInventoryHref(record.finding.scopeId.trim())}
                            className="ui-badge ui-badge--ghost"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}>
                            {resolveScopeLabel(record.finding.scopeType, record.finding.scopeId, siteNameById, orgDisplayName, deviceSummaryById)}
                          </a>
                        ) : (
                          <span className="ui-badge ui-badge--ghost">
                            {resolveScopeLabel(record.finding.scopeType, record.finding.scopeId, siteNameById, orgDisplayName, deviceSummaryById)}
                          </span>
                        )}
                        <span className="ui-badge ui-badge--ghost">
                          {record.finding.evidence?.length ? `${formatNumber(record.finding.evidence.length)} evidence` : 'No evidence'}
                        </span>
                        {record.finding.remediation?.commands?.length ? (
                          <span className="ui-badge ui-badge--ghost">Snippet available</span>
                        ) : null}
                      </div>
                      <h4>{findingDisplayTitle(record.finding)}</h4>
                      <p>{findingDisplayDetail(record.finding)}</p>
                      {findingDisplayAction(record.finding) ? <p className="posture-finding-card__action">Action: {findingDisplayAction(record.finding)}</p> : null}
                      {record.finding.remediation?.summary?.trim() ? (
                        <p className="posture-finding-card__action">Snippet: {record.finding.remediation.summary}</p>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <p className="posture-empty-copy">This run did not persist detailed findings.</p>
              )}
            </section>

            <section className="posture-detail-section">
              <div className="posture-section__header">
                <div>
                  <h3>Report</h3>
                  <p>Rendered operator-facing report captured for this posture run.</p>
                </div>
                {selectedRunReportStats ? (
                  <div className="posture-chip-group">
                    <span className="posture-chip posture-chip--neutral">{humanizeToken(selectedRun.report?.format || 'text')}</span>
                    {selectedRun.report?.schemaVersion ? (
                      <span className="posture-chip posture-chip--neutral">Schema {selectedRun.report.schemaVersion}</span>
                    ) : null}
                    <span className="posture-chip posture-chip--neutral">{selectedRunReportStats.lengthLabel}</span>
                    {selectedRunReportStats.headingCount > 0 ? (
                      <span className="posture-chip posture-chip--neutral">{formatNumber(selectedRunReportStats.headingCount)} sections</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {selectedRun.report?.content ? (
                selectedRun.report.format === 'markdown' ? (
                  <div className="posture-report posture-report--markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedRun.report.content}</ReactMarkdown>
                  </div>
                ) : (
                  <pre className="posture-report">{selectedRun.report.content}</pre>
                )
              ) : (
                <p className="posture-empty-copy">No rendered report was stored for this run.</p>
              )}
            </section>
          </div>
        ) : (
          <p>Select a run to inspect its full posture output.</p>
        )}
      </Drawer>
      <DeleteConfirmationModal
        open={pendingDeleteJob != null}
        title="Delete posture job?"
        description={`Delete posture job "${pendingDeleteJob?.name ?? ''}"? This cannot be undone.`}
        deleting={jobDeleteBusyId != null}
        onCancel={() => setPendingDeleteJob(null)}
        onConfirm={() => void confirmDeleteJob()}
      />
      {jobDeleteToast && (
        <StatusToast
          tone={jobDeleteToast.tone}
          message={jobDeleteToast.message}
          occurrence={jobDeleteToast}
          onDismiss={() => setJobDeleteToast(null)}
        />
      )}
    </OrgModuleLayout>
  );
};

interface RulePackSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: PostureRulePackDescriptor[];
  loading?: boolean;
  errorMessage?: string | null;
}

const RulePackSelector = ({ value, onChange, options, loading = false, errorMessage }: RulePackSelectorProps) => {
  const knownIds = new Set(options.map((item) => item.id));
  const items = [
    ...options,
    ...value
      .filter((rulePack) => !knownIds.has(rulePack))
      .map((rulePack) => ({
        id: rulePack,
        kind: 'unknown',
        label: `${humanizeToken(rulePack)} (Unknown)`
      }))
  ];

  return (
    <>
      <div className="posture-rule-pack-grid">
        {items.map((item) => {
          const rulePack = item.id;
          const checked = value.includes(rulePack);
          return (
            <label key={rulePack} className="posture-rule-pack">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  const next = checked ? value.filter((selected) => selected !== rulePack) : [...value, rulePack];
                  onChange(normalizeRulePacks(next));
                }}
              />
              <span>{item.label?.trim() || humanizeToken(rulePack)}</span>
            </label>
          );
        })}
      </div>
      {loading && options.length === 0 ? <p className="posture-empty-copy">Loading available rule packs…</p> : null}
      {errorMessage ? <p className="posture-empty-copy">Failed to load full rule-pack catalog; using built-in defaults. {errorMessage}</p> : null}
    </>
  );
};

interface PostureFilterBarProps {
  summary: string;
  controls: ReactNode[];
}

interface PostureFindingDetailProps {
  record: PostureFindingRecord;
  siteNameById: Record<string, string>;
  orgDisplayName: string;
  deviceSummaryById: Record<string, PostureDeviceSummary | null>;
}

const PostureFilterBar = ({ summary, controls }: PostureFilterBarProps) => (
  <div className="posture-filter-bar">
    <span className="posture-filter-bar__summary">{summary}</span>
    <div className="posture-filter-bar__controls">{controls}</div>
  </div>
);

const PostureFindingDetail = ({ record, siteNameById, orgDisplayName, deviceSummaryById }: PostureFindingDetailProps) => {
  const remediationVariants = useMemo(() => resolveRemediationVariants(record.finding.remediation), [record.finding.remediation]);
  const defaultRemediationVariant = useMemo(
    () => resolveDefaultRemediationVariant(record.finding.remediation, remediationVariants),
    [record.finding.remediation, remediationVariants]
  );
  const [activeRemediationVariant, setActiveRemediationVariant] = useState(
    normalizeRemediationVariantKind(defaultRemediationVariant?.kind ?? remediationVariants[0]?.kind)
  );

  useEffect(() => {
    setActiveRemediationVariant(normalizeRemediationVariantKind(defaultRemediationVariant?.kind ?? remediationVariants[0]?.kind));
  }, [defaultRemediationVariant?.kind, remediationVariants]);

  const selectedRemediationVariant =
    remediationVariants.find((variant) => normalizeRemediationVariantKind(variant.kind) === activeRemediationVariant) ?? defaultRemediationVariant ?? null;
  const selectedRemediationCommands = resolveRemediationCommands(selectedRemediationVariant);
  const remediationTabs = remediationVariants.map((variant) => ({
    id: normalizeRemediationVariantKind(variant.kind),
    label: remediationVariantLabel(variant.kind)
  }));

  return (
    <div className="posture-drawer">
      <section className="record-summary">
        <h3 className="record-summary__title">Finding summary</h3>
        <div className="record-summary__grid">
          <div>
            <span>Severity</span>
            <strong>{humanizeToken(record.finding.severity)}</strong>
          </div>
          <div>
            <span>Category</span>
            <strong>{findingCategoryLabel(record.finding)}</strong>
          </div>
          <div>
            <span>Scope</span>
            <strong>{renderScopeLabel(record.finding.scopeType, record.finding.scopeId, siteNameById, orgDisplayName, deviceSummaryById)}</strong>
          </div>
          <div>
            <span>Confidence</span>
            <strong>{record.finding.confidence?.trim() || '—'}</strong>
          </div>
          <div>
            <span>Rule ID</span>
            <strong className="record-summary__multiline">{record.finding.ruleId?.trim() || '—'}</strong>
          </div>
          <div>
            <span>Observed in run</span>
            <strong className="record-summary__multiline">{shortRunId(record.runId)}</strong>
          </div>
          <div>
            <span>Run status</span>
            <strong>{humanizeToken(record.status)}</strong>
          </div>
          <div>
            <span>Run time</span>
            <strong>{formatDateTime(record.requestedAt)}</strong>
          </div>
        </div>
      </section>

      <section className="posture-detail-section">
        <h3>Why this was flagged</h3>
        <div className="posture-detail-copy">{renderFindingDetailText(findingDisplayDetail(record.finding), siteNameById)}</div>
      </section>

      <section className="posture-detail-grid">
        <div className="posture-detail-section">
          <h3>Recommendation</h3>
          {findingDisplayAction(record.finding) ? (
            <p>{findingDisplayAction(record.finding)}</p>
          ) : (
            <p className="posture-empty-copy">No explicit recommendation was returned for this finding.</p>
          )}
        </div>
      </section>

      {selectedRemediationVariant ? (
        <section className="posture-detail-section">
          <h3>Candidate Remediation</h3>
          {selectedRemediationCommands.length ? <CopyField value={selectedRemediationCommands.join('\n')} label="Copy commands" variant="chip" /> : null}
          {remediationTabs.length > 1 ? (
            <Tabs items={remediationTabs} activeId={activeRemediationVariant} onChange={setActiveRemediationVariant} />
          ) : null}
          {selectedRemediationVariant.summary?.trim() ? <p>{selectedRemediationVariant.summary}</p> : null}
          {selectedRemediationCommands.length ? (
            <div className="posture-remediation-command-block">
              <div className="posture-remediation-command-block__header">
                <span>Suggested commands</span>
                <span>{formatNumber(selectedRemediationCommands.length)} line(s)</span>
              </div>
              <pre className="posture-report">{selectedRemediationCommands.join('\n')}</pre>
            </div>
          ) : null}
          {selectedRemediationVariant.proposal?.placeholders?.length ? (
            <>
              <h4>Parameters to fill</h4>
              <ul className="posture-bullet-list">
                {selectedRemediationVariant.proposal.placeholders.map((placeholder, index) => (
                  <li key={`${placeholder.token || 'placeholder'}-${index}`}>
                    <strong>{placeholder.token?.trim() || 'Placeholder'}</strong>
                    {placeholder.description?.trim() ? `: ${placeholder.description}` : ''}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {selectedRemediationVariant.caveats?.length ? (
            <>
              <h4>Review before use</h4>
              <ul className="posture-bullet-list">
                {selectedRemediationVariant.caveats.map((caveat, index) => (
                  <li key={`${caveat}-${index}`}>{caveat}</li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="posture-detail-section">
        <h3>Evidence</h3>
        {record.finding.evidence?.length ? (
          <div className="posture-detail-findings">
            {record.finding.evidence.map((evidence, index) => {
              const title = evidenceTitle(evidence, siteNameById);
              const summary = evidenceSummary(evidence, siteNameById);
              const highlights = evidenceHighlights(evidence, siteNameById);
              return (
                <article key={`${evidence.kind}-${evidence.locator}-${index}`} className="posture-finding-card">
                  <div className="posture-finding-card__header">
                    <span className="ui-badge ui-badge--ghost">{evidenceCategoryLabel(evidence.category)}</span>
                    {evidenceKindLabel(evidence.kind) !== title ? <span className="ui-badge ui-badge--ghost">{evidenceKindLabel(evidence.kind)}</span> : null}
                  </div>
                  <h4>{title}</h4>
                  {summary ? <p>{summary}</p> : null}
                  {highlights.length ? (
                    <ul className="posture-bullet-list posture-finding-card__action">
                      {highlights.map((item, highlightIndex) => (
                        <li key={`${item}-${highlightIndex}`}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : (
          <p className="posture-empty-copy">No structured evidence was stored for this finding.</p>
        )}
      </section>
    </div>
  );
};

const remediationVariantVisible = (variant?: PostureRemediationVariant | null) =>
  Boolean(
    variant?.summary?.trim() ||
      resolveRemediationCommands(variant).length ||
      variant?.caveats?.length ||
      variant?.proposal?.placeholders?.length
  );

const resolveRemediationVariants = (remediation?: PostureRemediation | null): PostureRemediationVariant[] => {
  if (!remediation) {
    return [];
  }
  const variants = (remediation.variants ?? []).filter(remediationVariantVisible);
  if (variants.length > 0) {
    return variants;
  }
  const topLevelCommands = (remediation.commands ?? []).map((command) => command?.trim()).filter(Boolean);
  const proposalCommands = (remediation.proposal?.changes ?? [])
    .flatMap((change) => change.diff ?? [])
    .filter((entry) => normalizeToken(entry.op) === 'add' || normalizeToken(entry.op) === 'set')
    .map((entry) => entry.line?.trim())
    .filter(Boolean) as string[];
  const hasTemplatePlaceholders = Boolean(remediation.proposal?.placeholders?.length);
  const sharedSummary = remediation.summary;
  const sharedCaveats = remediation.caveats;

  if (topLevelCommands.length > 0 && (hasTemplatePlaceholders || proposalCommands.length > 0)) {
    return [
      {
        kind: 'exact_config',
        summary: sharedSummary,
        commands: topLevelCommands,
        caveats: sharedCaveats
      },
      {
        kind: 'template',
        summary: sharedSummary,
        commands: proposalCommands.length ? proposalCommands : topLevelCommands,
        caveats: sharedCaveats,
        proposal: remediation.proposal
      }
    ].filter(remediationVariantVisible);
  }

  if (!remediationVariantVisible(remediation)) {
    return [];
  }
  return [
    {
      kind: hasTemplatePlaceholders ? 'template' : 'exact_config',
      summary: sharedSummary,
      commands: topLevelCommands,
      caveats: sharedCaveats,
      proposal: remediation.proposal
    }
  ];
};

const resolveDefaultRemediationVariant = (
  remediation: PostureRemediation | null | undefined,
  variants: PostureRemediationVariant[]
): PostureRemediationVariant | null => {
  if (variants.length === 0) {
    return null;
  }
  const configured = normalizeRemediationVariantKind(remediation?.defaultVariant, '');
  if (configured) {
    const matched = variants.find((variant) => normalizeRemediationVariantKind(variant.kind) === configured);
    if (matched) {
      return matched;
    }
  }
  return variants.find((variant) => normalizeRemediationVariantKind(variant.kind) === 'exact_config') ?? variants[0];
};

const remediationVariantLabel = (kind?: string) => (normalizeRemediationVariantKind(kind) === 'exact_config' ? 'Exact config' : 'Template');

const findingHasVisibleRemediation = (finding: PostureFinding): boolean => resolveRemediationVariants(finding.remediation).length > 0;

const readPostureRouteState = (): PostureRouteState => {
  if (typeof window === 'undefined') {
    return defaultPostureRouteState();
  }
  const params = new URLSearchParams(window.location.search);
  return {
    scopeType: params.get('scope') === 'site' ? 'site' : 'org',
    siteId: params.get('siteId')?.trim() ?? '',
    selectedRunId: params.get('selectedRunId')?.trim() ?? '',
    findingsIncludeSiteItems: params.get('findingIncludeSites') === '1',
    findingsSearch: params.get('findingSearch')?.trim() ?? '',
    findingsHotspotsOnly: params.get('findingHotspots') === '1',
    findingsHasEvidenceOnly: params.get('findingHasEvidence') === '1',
    findingsHasRemediationOnly: params.get('findingHasRemediation') === '1',
    findingsHasRecommendationOnly: params.get('findingHasRecommendation') === '1',
    findingsSeverityFilter: params.get('findingSeverity')?.trim() || 'all',
    findingsDomainFilter: params.get('findingDomain')?.trim() || 'all',
    findingsSort: normalizeFindingsSort(params.get('findingSort')),
    runsStatusFilter: params.get('runStatus')?.trim() || 'all',
    runsTriggerFilter: params.get('runTrigger')?.trim() || 'all',
    runsSort: normalizeRunsSort(params.get('runSort')),
    jobsStatusFilter: params.get('jobStatus')?.trim() || 'all',
    jobsScopeFilter: params.get('jobScope')?.trim() || 'all',
    jobsSort: normalizeJobsSort(params.get('jobSort'))
  };
};

const defaultPostureRouteState = (): PostureRouteState => ({
  scopeType: 'org',
  siteId: '',
  selectedRunId: '',
  findingsIncludeSiteItems: false,
  findingsSearch: '',
  findingsHotspotsOnly: false,
  findingsHasEvidenceOnly: false,
  findingsHasRemediationOnly: false,
  findingsHasRecommendationOnly: false,
  findingsSeverityFilter: 'all',
  findingsDomainFilter: 'all',
  findingsSort: 'severity_desc',
  runsStatusFilter: 'all',
  runsTriggerFilter: 'all',
  runsSort: 'requested_desc',
  jobsStatusFilter: 'all',
  jobsScopeFilter: 'all',
  jobsSort: 'updated_desc'
});

const buildPostureRouteSearch = (currentSearch: string, state: PostureRouteState): string => {
  const params = new URLSearchParams(currentSearch);
  setQueryParam(params, 'scope', state.scopeType === 'site' ? 'site' : null);
  setQueryParam(params, 'siteId', state.scopeType === 'site' ? state.siteId : null);
  setQueryParam(params, 'selectedRunId', state.selectedRunId || null);
  setQueryParam(params, 'findingIncludeSites', state.scopeType === 'org' && state.findingsIncludeSiteItems ? '1' : null);
  setQueryParam(params, 'findingSearch', state.findingsSearch || null);
  setQueryParam(params, 'findingHotspots', state.findingsHotspotsOnly ? '1' : null);
  setQueryParam(params, 'findingHasEvidence', state.findingsHasEvidenceOnly ? '1' : null);
  setQueryParam(params, 'findingHasRemediation', state.findingsHasRemediationOnly ? '1' : null);
  setQueryParam(params, 'findingHasRecommendation', state.findingsHasRecommendationOnly ? '1' : null);
  setQueryParam(params, 'findingSeverity', state.findingsSeverityFilter !== 'all' ? state.findingsSeverityFilter : null);
  setQueryParam(params, 'findingDomain', state.findingsDomainFilter !== 'all' ? state.findingsDomainFilter : null);
  setQueryParam(params, 'findingSort', state.findingsSort !== 'severity_desc' ? state.findingsSort : null);
  setQueryParam(params, 'runStatus', state.runsStatusFilter !== 'all' ? state.runsStatusFilter : null);
  setQueryParam(params, 'runTrigger', state.runsTriggerFilter !== 'all' ? state.runsTriggerFilter : null);
  setQueryParam(params, 'runSort', state.runsSort !== 'requested_desc' ? state.runsSort : null);
  setQueryParam(params, 'jobStatus', state.jobsStatusFilter !== 'all' ? state.jobsStatusFilter : null);
  setQueryParam(params, 'jobScope', state.jobsScopeFilter !== 'all' ? state.jobsScopeFilter : null);
  setQueryParam(params, 'jobSort', state.jobsSort !== 'updated_desc' ? state.jobsSort : null);
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const buildPostureRouteHref = (pathname: string, state: PostureRouteState) => {
  const currentSearch = typeof window === 'undefined' ? '' : window.location.search;
  return `${pathname}${buildPostureRouteSearch(currentSearch, state)}`;
};

const setQueryParam = (params: URLSearchParams, key: string, value: string | null) => {
  if (!value) {
    params.delete(key);
    return;
  }
  params.set(key, value);
};

const buildPostureResource = (
  resource: string,
  scopeType: PostureScopeType,
  scopeId: string,
  extras?: Record<string, string | number | boolean | null | undefined>
) => {
  const params = new URLSearchParams();
  if (scopeType === 'org') {
    params.set('scopeType', 'org');
  } else if (scopeId.trim()) {
    params.set('scopeType', 'site');
    params.set('scopeId', scopeId.trim());
  }
  Object.entries(extras ?? {}).forEach(([key, value]) => {
    if (value == null || value === '') {
      return;
    }
    params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `${resource}?${query}` : resource;
};

async function fetchAllChildRuns(
  basePath: string,
  orgId: string,
  parentRunId: string,
  fetchOrgResource: <T,>(basePath: string, orgId: string, resource: string) => Promise<T>
): Promise<PostureRunList> {
  const limit = 200;
  const items: PostureRunView[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (items.length < total) {
    const page = await fetchOrgResource<PostureRunList>(
      basePath,
      orgId,
      `posture/runs?parentRunId=${encodeURIComponent(parentRunId)}&limit=${limit}&offset=${offset}&sortBy=requestedAt&sortDesc=true`
    );
    const pageItems = page.items ?? [];
    items.push(...pageItems);
    total = Math.max(0, page.total ?? pageItems.length);
    if (pageItems.length < limit) {
      break;
    }
    offset += pageItems.length;
  }

  return {
    items,
    total: Number.isFinite(total) ? total : items.length,
    limit,
    offset: 0
  };
}

const humanizeToken = (value?: string | null) => {
  const token = (value ?? '').trim();
  if (!token) {
    return '—';
  }
  return token
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeRulePacks = (value: string[]) => {
  const seen = new Set<string>();
  const normalized: string[] = [];
  value.forEach((item) => {
    const token = item.trim();
    if (!token || seen.has(token)) {
      return;
    }
    seen.add(token);
    normalized.push(token);
  });
  return normalized;
};

const buildFallbackRulePackDescriptors = (): PostureRulePackDescriptor[] => [
  { id: 'foundation', kind: 'coarse', label: 'Foundational Hygiene', defaultEnabled: true },
  { id: 'management', kind: 'coarse', label: 'Management Hygiene', defaultEnabled: true },
  { id: 'topology', kind: 'coarse', label: 'Topology Consistency', defaultEnabled: true },
  { id: 'wan', kind: 'coarse', label: 'WAN Resiliency', defaultEnabled: true },
  { id: 'control_plane', kind: 'coarse', label: 'Control-Plane Posture', defaultEnabled: true },
  { id: 'standardization', kind: 'coarse', label: 'Org Standardization', defaultEnabled: true }
];

const isSelectableRulePack = (rulePack: Partial<Pick<PostureRulePackDescriptor, 'id' | 'kind'>>) =>
  rulePack.kind === 'coarse' || (!rulePack.kind && Boolean(rulePack.id && DEFAULT_RULE_PACK_ID_SET.has(rulePack.id)));

const resolveRulePackLabel = (rulePack: string, labelById?: Record<string, string>) => labelById?.[rulePack] || humanizeToken(rulePack);

const effectiveRulePacks = (value?: string[] | null, defaultRulePacks: readonly string[] = DEFAULT_RULE_PACKS) =>
  value?.length ? normalizeRulePacks(value) : [...defaultRulePacks];

const defaultRulePackDescription = (defaultRulePacks: readonly string[] = DEFAULT_RULE_PACKS, labelById?: Record<string, string>) =>
  defaultRulePacks.map((rulePack) => resolveRulePackLabel(rulePack, labelById)).join(', ');

const formatVisibleRowsSummary = (page: number, pageSize: number, visibleCount: number, totalCount: number, label: string) => {
  if (visibleCount <= 0 || totalCount <= 0) {
    return `0 of ${formatNumber(totalCount)} ${label}`;
  }
  const safePage = Math.max(1, page);
  const safePageSize = Math.max(1, pageSize || DEFAULT_TABLE_PAGE_SIZE);
  const start = (safePage - 1) * safePageSize + 1;
  const end = Math.min(totalCount, start + visibleCount - 1);
  return `Showing ${formatNumber(start)}-${formatNumber(end)} of ${formatNumber(totalCount)} ${label}`;
};

const formatRulePackSummary = (value?: string[] | null, defaultRulePacks: readonly string[] = DEFAULT_RULE_PACKS, labelById?: Record<string, string>) => {
  const packs = effectiveRulePacks(value, defaultRulePacks);
  if (value?.length) {
    return packs.map((rulePack) => resolveRulePackLabel(rulePack, labelById)).join(', ');
  }
  return `Default (${packs.length} packs)`;
};

const normalizeToken = (value?: string | null) => (value ?? '').trim().toLowerCase();

const firstNonEmptyArray = <T,>(...candidates: Array<T[] | null | undefined>): T[] => {
  for (const candidate of candidates) {
    if (candidate?.length) {
      return candidate;
    }
  }
  return [];
};

const normalizeIdentifier = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const normalizeRemediationVariantKind = (value?: string | null, fallback = 'template') => normalizeToken(value) || fallback;

const uniqueTokens = (values: Array<string | null | undefined>) =>
  Array.from(
    values.reduce<Map<string, string>>((acc, value) => {
      const normalized = normalizeToken(value);
      if (!normalized || acc.has(normalized)) {
        return acc;
      }
      acc.set(normalized, normalized);
      return acc;
    }, new Map<string, string>())
  )
    .map(([key]) => key)
    .sort(compareTextAsc);

const normalizeFindingsSort = (value?: string | null): 'severity_desc' | 'requested_desc' | 'domain_asc' => {
  switch (value) {
    case 'requested_desc':
    case 'domain_asc':
      return value;
    default:
      return 'severity_desc';
  }
};

const resolveRemediationCommands = (remediation?: Pick<PostureRemediation, 'commands' | 'proposal'> | null): string[] => {
  if (!remediation) {
    return [];
  }
  const directCommands =
    remediation.commands
      ?.filter((command): command is string => typeof command === 'string')
      .map((command) => command.trim())
      .filter((command) => command.length > 0) ?? [];
  if (directCommands.length > 0) {
    return directCommands;
  }
  return (
    remediation.proposal?.changes
      ?.flatMap((change) => change.diff ?? [])
      .map((entry) => ({
        op: entry.op,
        text: typeof entry.line === 'string' ? entry.line : entry.value
      }))
      .filter((entry) => normalizeToken(entry.op) === 'add' && typeof entry.text === 'string')
      .map((entry) => entry.text?.trim() ?? '')
      .filter(Boolean) ?? []
  );
};

const normalizeRunsSort = (value?: string | null): 'requested_desc' | 'score_desc' | 'status_asc' => {
  switch (value) {
    case 'score_desc':
    case 'status_asc':
      return value;
    default:
      return 'requested_desc';
  }
};

const normalizeJobsSort = (value?: string | null): 'updated_desc' | 'name_asc' | 'scope_asc' => {
  switch (value) {
    case 'name_asc':
    case 'scope_asc':
      return value;
    default:
      return 'updated_desc';
  }
};

const shortRunId = (value: string) => (value ? value.slice(0, 8) : '—');

const looksLikeUuid = (value?: string | null) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test((value ?? '').trim());

const firstPresent = (...values: Array<string | null | undefined>) => values.find((value) => (value ?? '').trim().length > 0)?.trim() ?? '';

const formatSentenceList = (values: Array<string | null | undefined>) => {
  const normalized = values.map((value) => (value ?? '').trim()).filter(Boolean);
  if (!normalized.length) {
    return '';
  }
  if (normalized.length === 1) {
    return normalized[0]!;
  }
  if (normalized.length === 2) {
    return `${normalized[0]} and ${normalized[1]}`;
  }
  return `${normalized.slice(0, -1).join(', ')}, and ${normalized[normalized.length - 1]}`;
};

const humanizeCoverageInputType = (value?: string | null) => {
  const token = (value ?? '').trim();
  if (!token) {
    return '';
  }
  switch (normalizeToken(value)) {
    case 'running_configs':
      return 'running configuration snapshots';
    case 'topology_edges':
      return 'topology relationships';
    case 'endpoint_attachments':
      return 'endpoint attachment data';
    case 'provider_edge_ports':
      return 'provider-edge port mappings';
    default:
      return humanizeToken(value).toLowerCase();
  }
};

const evidenceCategoryLabel = (category?: string | null) => {
  switch (normalizeToken(category)) {
    case 'affected_resource':
      return 'Affected resource';
    case 'parsed_inventory':
      return 'Observed configuration';
    case 'coverage_gap':
      return 'Coverage limitation';
    case 'rule_reference':
      return 'Related check';
    default:
      return 'Evidence';
  }
};

const evidenceKindLabel = (kind?: string | null) => {
  switch (normalizeToken(kind)) {
    case 'rule':
      return 'Linked rule';
    case 'affected_device':
      return 'Affected device';
    case 'affected_interface':
      return 'Affected interface';
    case 'endpoint_attachment':
      return 'Endpoint attachment';
    case 'topology_coverage':
      return 'Topology coverage';
    case 'topology_edge':
      return 'Observed topology relationship';
    case 'running_config_coverage':
      return 'Running configuration coverage';
    case 'missing_running_configs':
      return 'Missing running configuration';
    case 'aaa_methods':
      return 'AAA methods observed';
    case 'aaa_remote_backends':
      return 'Centralized AAA backends observed';
    case 'local_users':
      return 'Local fallback accounts observed';
    case 'management_protocols':
      return 'Management access observed';
    case 'snmp_communities':
      return 'SNMP communities observed';
    case 'snmp_trap_targets':
      return 'SNMP trap targets observed';
    case 'logging_targets':
      return 'Remote logging targets observed';
    case 'time_servers':
      return 'Time sources observed';
    case 'interface_description':
      return 'Interface description observed';
    case 'interface_role':
      return 'Interface role observed';
    case 'interface_address':
      return 'Interface addressing observed';
    case 'interface_state':
      return 'Interface state observed';
    case 'interface_transport':
      return 'Interface transport observed';
    case 'interface_vlans':
      return 'Interface VLANs observed';
    case 'interface_bundle':
      return 'Interface bundle observed';
    case 'interface_policy':
      return 'Interface policy observed';
    case 'interface_attachment':
      return 'Interface attachment observed';
    case 'static_route':
      return 'Static route observed';
    case 'bgp_neighbor':
      return 'BGP neighbor observed';
    case 'bgp_redistribution':
      return 'BGP redistribution observed';
    case 'device_role':
      return 'Device role observed';
    case 'software_version':
      return 'Software version observed';
    case 'feature_set':
      return 'Feature set observed';
    case 'provider_edge_identity':
      return 'Provider edge identified';
    case 'provider_edge_drift':
      return 'Provider edge drift observed';
    case 'provider_edge_readiness':
      return 'Provider edge readiness observed';
    case 'provider_edge_outage':
      return 'Provider edge outage observed';
    case 'provider_edge_truth':
      return 'Provider edge baseline observed';
    case 'posture_summary':
      return 'Posture summary';
    case 'posture_finding':
      return 'Related posture finding';
    default:
      return humanizeToken(kind);
  }
};

const resolveEvidenceSubject = (evidence: PostureEvidence, siteNameById: Record<string, string>) => {
  const metadata = evidence.metadata ?? {};
  const referenceLabel = firstPresent(...(evidence.references ?? []).map((reference) => reference.label));
  const resourceName = firstPresent(metadata.resource_name, metadata.site_name, referenceLabel);
  if (resourceName && !looksLikeUuid(resourceName)) {
    return resourceName;
  }
  const resourceType = normalizeToken(metadata.resource_type);
  const resourceId = firstPresent(metadata.resource_id, ...(evidence.references ?? []).map((reference) => reference.scopeId));
  if (resourceType === 'site' && resourceId) {
    return siteNameById[resourceId] ?? `Site ${resourceId.slice(0, 8)}`;
  }
  const locator = evidence.locator?.trim() ?? '';
  if (locator && !looksLikeUuid(locator)) {
    return locator;
  }
  if (resourceId && !looksLikeUuid(resourceId)) {
    return resourceType ? `${humanizeToken(resourceType)} ${resourceId}` : resourceId;
  }
  return '';
};

const evidenceTitle = (evidence: PostureEvidence, siteNameById: Record<string, string>) => {
  const subject = resolveEvidenceSubject(evidence, siteNameById);
  const kindLabel = evidenceKindLabel(evidence.kind);
  switch (normalizeToken(evidence.kind)) {
    case 'affected_device':
    case 'affected_interface':
      return kindLabel;
    case 'posture_summary':
      return normalizeToken(evidence.category) === 'coverage_gap' ? 'Coverage limitation' : kindLabel;
    case 'rule':
      return 'Related evaluation check';
    default:
      return subject ? `${kindLabel} for ${subject}` : kindLabel;
  }
};

const kindLabelSentence = (kind?: string | null) => {
  const label = evidenceKindLabel(kind);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const evidenceSummary = (evidence: PostureEvidence, siteNameById: Record<string, string>) => {
  const subject = resolveEvidenceSubject(evidence, siteNameById);
  const metadata = evidence.metadata ?? {};
  const values = (evidence.values ?? []).map((value) => value.trim()).filter(Boolean);
  const note = evidence.note?.trim() ?? '';
  switch (normalizeToken(evidence.kind)) {
    case 'affected_device':
    case 'affected_interface':
      return subject ? `This finding applies to ${subject}.` : 'This finding applies to the affected resource.';
    case 'aaa_remote_backends':
      return values.length ? `${subject || 'This device'} currently points to centralized AAA backends ${formatSentenceList(values)}.` : note;
    case 'aaa_methods':
      return values.length ? `${subject || 'This device'} currently uses AAA methods ${formatSentenceList(values)}.` : note;
    case 'management_protocols':
      return values.length ? `${subject || 'This device'} currently allows ${formatSentenceList(values)} for management access.` : note;
    case 'logging_targets':
      return values.length ? `${subject || 'This device'} currently sends logs to ${formatSentenceList(values)}.` : note;
    case 'time_servers':
      return values.length ? `${subject || 'This device'} currently syncs time with ${formatSentenceList(values)}.` : note;
    case 'local_users':
      return values.length ? `${subject || 'This device'} currently has local fallback accounts ${formatSentenceList(values)}.` : note;
    case 'snmp_communities':
      return values.length ? `${subject || 'This device'} currently exposes SNMP communities ${formatSentenceList(values)}.` : note;
    case 'snmp_trap_targets':
      return values.length ? `${subject || 'This device'} currently sends SNMP traps to ${formatSentenceList(values)}.` : note;
    case 'posture_summary': {
      const inputType = humanizeCoverageInputType(metadata.input_type);
      const coverageStatus = normalizeToken(metadata.coverage_status);
      if (normalizeToken(evidence.category) === 'coverage_gap' && inputType) {
        if (coverageStatus && coverageStatus !== 'complete') {
          return `${inputType.charAt(0).toUpperCase()}${inputType.slice(1)} are currently ${coverageStatus}.`;
        }
        return `${inputType.charAt(0).toUpperCase()}${inputType.slice(1)} are part of the current coverage picture.`;
      }
      return note || (subject ? `${kindLabelSentence(evidence.kind)} for ${subject}.` : '');
    }
    case 'rule':
      return note || 'This item links the higher-level finding back to the underlying evaluation rule.';
    default:
      if (note) {
        return note;
      }
      if (values.length) {
        return subject ? `${subject} currently shows ${formatSentenceList(values)}.` : `Observed ${formatSentenceList(values)}.`;
      }
      return subject ? `Observed on ${subject}.` : '';
  }
};

const replaceKnownSiteIds = (text: string, siteNameById: Record<string, string>) =>
  text.replace(uuidTokenPattern, (match) => siteNameById[match.toLowerCase()] ?? siteNameById[match] ?? match);

const evidenceHighlights = (evidence: PostureEvidence, siteNameById: Record<string, string>) => {
  const highlights: string[] = [];
  const metadata = evidence.metadata ?? {};
  const values = (evidence.values ?? []).map((value) => value.trim()).filter(Boolean);
  const note = evidence.note?.trim() ?? '';
  const referenceItems = (evidence.references ?? []).map((reference) => firstPresent(reference.label, reference.detail)).filter(Boolean);
  if (normalizeToken(evidence.category) === 'coverage_gap') {
    const expected = parsePositiveInt(metadata.expected_count);
    const observed = parsePositiveInt(metadata.observed_count);
    if (expected > 0 || observed > 0) {
      highlights.push(`Observed ${formatNumber(observed)} of expected ${formatNumber(expected || observed)} sources for this check.`);
    }
    const inputType = humanizeCoverageInputType(metadata.input_type);
    const coverageStatus = normalizeToken(metadata.coverage_status);
    if (inputType && coverageStatus && coverageStatus !== 'complete') {
      highlights.push(`Coverage status: ${inputType} are ${coverageStatus}.`);
    }
  }
  if (note && note.toLowerCase() !== 'coverage input status' && note !== evidenceSummary(evidence, siteNameById)) {
    highlights.push(note);
  }
  if (
    values.length &&
    !new Set(['aaa_remote_backends', 'aaa_methods', 'management_protocols', 'logging_targets', 'time_servers', 'local_users', 'snmp_communities', 'snmp_trap_targets']).has(
      normalizeToken(evidence.kind)
    )
  ) {
    highlights.push(`Observed values: ${values.map((value) => replaceKnownSiteIds(value, siteNameById)).join(', ')}.`);
  }
  if (referenceItems.length) {
    highlights.push(`Related resources: ${formatSentenceList(referenceItems)}.`);
  }
  return highlights;
};

const formatOpaqueIdentifier = (value?: string | null, visibleLength = 8) => {
  const token = (value ?? '').trim();
  if (!token) {
    return '';
  }
  if (token.length <= visibleLength) {
    return token;
  }
  if (/^[a-f0-9-]{16,}$/i.test(token) || (/^[a-z0-9._-]+$/i.test(token) && token.length > 20)) {
    return token.slice(0, visibleLength);
  }
  return token;
};

const truncateText = (value: string, maxLength: number) => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};

const findingRowId = (record: PostureFindingRecord) =>
  `${record.runId}-${record.finding.ruleId}-${record.finding.scopeId}-${record.finding.scopeType}-${record.requestedAt ?? ''}`;

const buildFindingRecordFromRun = (run: PostureRunView, finding: PostureFinding): PostureFindingRecord => ({
  runId: run.runId,
  orgId: run.orgId,
  scopeType: run.scopeType,
  scopeId: run.scopeId,
  triggerType: run.triggerType,
  status: run.status,
  coverageStatus: run.coverageStatus,
  requestedAt: run.requestedAt,
  finishedAt: run.finishedAt,
  schemaVersion: run.report?.schemaVersion ?? run.summary?.metadata?.schemaVersion,
  finding
});

const findingRouteKey = (finding: PostureFinding) => JSON.stringify([finding.ruleId, finding.scopeType, finding.scopeId]);

const summarizeReportStats = (report?: PostureReport | null): PostureReportStats | null => {
  const content = report?.content?.trim();
  if (!content) {
    return null;
  }
  const lineCount = content.split(/\r?\n/).length;
  const headingCount = normalizeToken(report?.format) === 'markdown' ? (content.match(/^#{1,6}\s+/gm) ?? []).length : 0;
  return {
    lengthLabel: `${formatNumber(lineCount)} lines`,
    headingCount
  };
};

const summarizeCoverageNotes = (summary?: PostureSummary | null, siteNameById: Record<string, string> = {}): PostureCoverageNote[] => {
  if (!summary) {
    return [];
  }
  const notes: PostureCoverageNote[] = [];
  const metadata = summary.metadata ?? {};

  const missingInputs = splitDelimitedMetadata(metadata['missing_inputs'], ',');
  if (missingInputs.length > 0) {
    notes.push({
      title: 'Missing inputs',
      detail: missingInputs.map(humanizeToken).join(', ')
    });
  }

  const sitesMissing = parsePositiveInt(metadata['sites_missing']);
  if (sitesMissing > 0) {
    notes.push({
      title: 'Sites missing from rollup',
      detail: `${formatNumber(sitesMissing)} site${sitesMissing === 1 ? '' : 's'} did not make it into the org-level result due to start failures or fan-out issues.`
    });
  }

  const sitesDegraded = parsePositiveInt(metadata['sites_degraded']);
  if (sitesDegraded > 0) {
    notes.push({
      title: 'Degraded sites',
      detail: `${formatNumber(sitesDegraded)} site${sitesDegraded === 1 ? '' : 's'} completed with partial coverage.`
    });
  }

  const sitesCompletedWithGaps = parsePositiveInt(metadata['sites_completed_with_gaps']);
  if (sitesCompletedWithGaps > 0) {
    notes.push({
      title: 'Runs completed with gaps',
      detail: `${formatNumber(sitesCompletedWithGaps)} site${sitesCompletedWithGaps === 1 ? '' : 's'} completed with gaps rather than a clean completed status.`
    });
  }

  const weakestDomains = splitSummaryMetadata(metadata['weakest_domains']);
  if (weakestDomains.length > 0) {
    notes.push({
      title: 'Weakest domains',
      detail: weakestDomains.join('; ')
    });
  }

  const worstSites = splitSummaryMetadata(metadata['worst_sites']);
  if (worstSites.length > 0) {
    notes.push({
      title: 'Most affected sites',
      items: worstSites.map((entry) => summarizeWorstSiteEntry(entry, siteNameById))
    });
  }

  if (notes.length === 0 && normalizeToken(summary.coverageStatus) === 'degraded') {
    notes.push({
      title: 'Coverage degraded',
      detail: 'The run completed with bounded results, but some inputs were missing, stale, or incomplete.'
    });
  }

  if (notes.length === 0 && normalizeToken(summary.coverageStatus) === 'unknown') {
    notes.push({
      title: 'Coverage unknown',
      detail: 'The system could not determine whether the posture result has complete supporting inputs.'
    });
  }

  return notes;
};

const normalizeComparableText = (value?: string | null) =>
  (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const comparableTextIncludes = (haystack?: string | null, needle?: string | null) => {
  const normalizedNeedle = normalizeComparableText(needle);
  if (!normalizedNeedle) {
    return false;
  }
  return normalizeComparableText(haystack).includes(normalizedNeedle);
};

const summaryFindingSupportsLabel = (finding: PostureFinding, label: string) => {
  if (comparableTextIncludes(findingDisplayAction(finding), label)) {
    return true;
  }
  if (comparableTextIncludes(findingDisplayTitle(finding), label) || comparableTextIncludes(findingDisplayDetail(finding), label)) {
    return true;
  }
  return (finding.evidence ?? []).some((evidence) => {
    if ((evidence.values ?? []).some((value) => comparableTextIncludes(value, label))) {
      return true;
    }
    return (evidence.references ?? []).some((reference) => comparableTextIncludes(reference.label, label) || comparableTextIncludes(reference.detail, label));
  });
};

const derivedSummaryItemFindingRuleIds = (item: PostureSummaryItem, findings: PostureFindingRecord[]): string[] => {
  const label = item.label?.trim() ?? '';
  if (!label || findings.length === 0) {
    return [];
  }
  const itemKind = normalizeToken(item.kind);
  const matchedRuleIds = findings
    .filter((record) => {
      const ruleId = normalizeIdentifier(record.finding.ruleId);
      if (!ruleId) {
        return false;
      }
      if (itemKind === 'priority_action') {
        return comparableTextIncludes(findingDisplayAction(record.finding), label) || summaryFindingSupportsLabel(record.finding, label);
      }
      if (itemKind === 'strength') {
        return summaryFindingSupportsLabel(record.finding, label);
      }
      return summaryFindingSupportsLabel(record.finding, label);
    })
    .map((record) => normalizeIdentifier(record.finding.ruleId));
  return Array.from(new Set(matchedRuleIds.filter(Boolean)));
};

const summaryItemFindingRuleIds = (item?: PostureSummaryItem | null, findings: PostureFindingRecord[] = []): string[] => {
  const seen = new Set<string>();
  const explicitRuleIds = (item?.ruleLinks ?? [])
    .map((link) => normalizeIdentifier(link.ruleId))
    .filter((ruleId) => {
      if (!ruleId || seen.has(ruleId)) {
        return false;
      }
      seen.add(ruleId);
      return true;
    });
  if (explicitRuleIds.length > 0 || !item) {
    return explicitRuleIds;
  }
  return derivedSummaryItemFindingRuleIds(item, findings);
};

const countSummaryItemFindingMatches = (item: PostureSummaryItem, findings: PostureFindingRecord[]) => {
  const ruleIds = new Set(summaryItemFindingRuleIds(item, findings));
  if (ruleIds.size === 0) {
    return 0;
  }
  return Array.from(new Set(findings.map((record) => normalizeIdentifier(record.finding.ruleId)).filter((ruleId) => ruleIds.has(ruleId)))).length;
};

const coverageNoteSiteIds = (note: PostureCoverageNote, siteNameById: Record<string, string>): string[] => {
  const reverseSiteNameById = Object.entries(siteNameById).reduce<Record<string, string>>((acc, [siteId, siteName]) => {
    acc[normalizeComparableText(siteName)] = siteId;
    return acc;
  }, {});
  const siteIds = new Set<string>();
  for (const item of note.items ?? []) {
    const href = item.href?.trim() ?? '';
    if (href) {
      const queryIndex = href.indexOf('?');
      const params = new URLSearchParams(queryIndex >= 0 ? href.slice(queryIndex + 1) : '');
      const siteId = params.get('siteId')?.trim() ?? '';
      if (siteId) {
        siteIds.add(siteId);
        continue;
      }
    }
    const mappedSiteId = reverseSiteNameById[normalizeComparableText(item.label)];
    if (mappedSiteId) {
      siteIds.add(mappedSiteId);
    }
  }
  return [...siteIds];
};

const coverageNoteDomains = (note: PostureCoverageNote, findings: PostureFindingRecord[]): string[] => {
  const noteText = normalizeComparableText([note.title, note.detail, ...(note.items ?? []).map((item) => `${item.label} ${item.detail ?? ''}`)].join(' '));
  if (!noteText) {
    return [];
  }
  return Array.from(
    new Set(
      findings
        .map((record) => normalizeToken(record.finding.domain))
        .filter(Boolean)
        .filter((domain) => noteText.includes(normalizeComparableText(domain)) || noteText.includes(normalizeComparableText(humanizeToken(domain))))
    )
  );
};

const derivedCoverageNoteFindingRuleIds = (
  note: PostureCoverageNote,
  findings: PostureFindingRecord[],
  siteNameById: Record<string, string>
): string[] => {
  if (findings.length === 0) {
    return [];
  }
  const matchingDomains = new Set(coverageNoteDomains(note, findings));
  const matchingSiteIds = new Set(coverageNoteSiteIds(note, siteNameById));
  const noteText = [note.title, note.detail, ...(note.items ?? []).map((item) => `${item.label} ${item.detail ?? ''}`)].join(' ').trim();
  const matchedRuleIds = findings
    .filter((record) => {
      const scopeType = normalizeToken(record.finding.scopeType);
      const scopeId = record.finding.scopeId?.trim() ?? '';
      if (matchingSiteIds.size > 0 && scopeType === 'site' && matchingSiteIds.has(scopeId)) {
        return true;
      }
      if (matchingDomains.size > 0 && matchingDomains.has(normalizeToken(record.finding.domain))) {
        return true;
      }
      return noteText ? summaryFindingSupportsLabel(record.finding, noteText) : false;
    })
    .map((record) => normalizeIdentifier(record.finding.ruleId));
  return Array.from(new Set(matchedRuleIds.filter(Boolean)));
};

const coverageNoteFindingRuleIds = (
  note?: PostureCoverageNote | null,
  findings: PostureFindingRecord[] = [],
  siteNameById: Record<string, string> = {}
): string[] => {
  const seen = new Set<string>();
  const explicitRuleIds = (note?.ruleLinks ?? [])
    .map((link) => normalizeIdentifier(link.ruleId))
    .filter((ruleId) => {
      if (!ruleId || seen.has(ruleId)) {
        return false;
      }
      seen.add(ruleId);
      return true;
    });
  if (explicitRuleIds.length > 0 || !note) {
    return explicitRuleIds;
  }
  return derivedCoverageNoteFindingRuleIds(note, findings, siteNameById);
};

const countCoverageNoteFindingMatches = (note: PostureCoverageNote, findings: PostureFindingRecord[], siteNameById: Record<string, string> = {}) => {
  const ruleIds = new Set(coverageNoteFindingRuleIds(note, findings, siteNameById));
  if (ruleIds.size === 0) {
    return 0;
  }
  return Array.from(new Set(findings.map((record) => normalizeIdentifier(record.finding.ruleId)).filter((ruleId) => ruleIds.has(ruleId)))).length;
};

const parsePositiveInt = (value?: string | null) => {
  const parsed = Number.parseInt((value ?? '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const splitDelimitedMetadata = (value: string | undefined, separator: string) =>
  (value ?? '')
    .split(separator)
    .map((item) => item.trim())
    .filter(Boolean);

const splitSummaryMetadata = (value?: string | null) =>
  (value ?? '')
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);

const uuidTokenPattern = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

const summarizeWorstSiteEntry = (entry: string, siteNameById: Record<string, string>): PostureCoverageNoteItem => {
  const separatorIndex = entry.indexOf(':');
  const siteId = (separatorIndex >= 0 ? entry.slice(0, separatorIndex) : entry).trim();
  const detail = (separatorIndex >= 0 ? entry.slice(separatorIndex + 1) : '').trim();
  if (!siteId) {
    return {
      key: entry,
      label: entry
    };
  }

  const routeState = readPostureRouteState();
  return {
    key: entry,
    label: siteNameById[siteId] ?? `Site ${siteId.slice(0, 8)}`,
    href: buildPostureRouteHref('/app/posture', {
      ...routeState,
      scopeType: 'site',
      siteId,
      selectedRunId: ''
    }),
    detail
  };
};

const renderFindingDetailInlineText = (detail: string, siteNameById: Record<string, string>): ReactNode => {
  const text = detail?.trim();
  if (!text) {
    return '—';
  }
  const routeState = readPostureRouteState();
  const parts: ReactNode[] = [];
  let cursor = 0;
  let matchedSite = false;

  text.replace(uuidTokenPattern, (match, offset: number) => {
    const siteId = match.toLowerCase();
    const siteName = siteNameById[siteId] ?? siteNameById[match];
    if (!siteName) {
      return match;
    }
    matchedSite = true;
    if (offset > cursor) {
      parts.push(text.slice(cursor, offset));
    }
    parts.push(
      <a
        key={`${siteId}-${offset}`}
        href={buildPostureRouteHref('/app/posture', {
          ...routeState,
          scopeType: 'site',
          siteId,
          selectedRunId: ''
        })}
        className="link-button">
        {siteName}
      </a>
    );
    cursor = offset + match.length;
    return match;
  });

  if (!matchedSite) {
    return text;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return <>{parts}</>;
};

const renderFindingDetailText = (detail: string, siteNameById: Record<string, string>): ReactNode => {
  const text = detail?.trim();
  if (!text) {
    return '—';
  }

  const firstSemicolon = text.indexOf(';');
  const firstColon = text.indexOf(':');
  const hasDelimitedList = firstSemicolon >= 0;
  const hasIntroPrefix = firstColon >= 0 && (firstSemicolon < 0 || firstColon < firstSemicolon);

  if (hasDelimitedList) {
    const intro = hasIntroPrefix ? text.slice(0, firstColon + 1).trim() : '';
    const remainder = hasIntroPrefix ? text.slice(firstColon + 1).trim() : text;
    const items = remainder
      .split(';')
      .map((item) => item.trim())
      .filter(Boolean);

    if (items.length >= 2) {
      return (
        <>
          {intro ? <p>{renderFindingDetailInlineText(intro, siteNameById)}</p> : null}
          <ul className="posture-detail-list">
            {items.map((item, index) => (
              <li key={`${item}-${index}`}>{renderFindingDetailInlineText(item, siteNameById)}</li>
            ))}
          </ul>
        </>
      );
    }
  }

  return <p>{renderFindingDetailInlineText(text, siteNameById)}</p>;
};

const renderEvidenceValues = (evidence: PostureEvidence, siteNameById: Record<string, string>): ReactNode => {
  if (evidence.references?.length) {
    return evidence.references.map((reference, index) => {
      const scopeType = normalizeToken(reference.scopeType);
      const scopeId = reference.scopeId?.trim() ?? '';
      const label = reference.label?.trim() || (scopeType === 'site' && scopeId ? siteNameById[scopeId] ?? `Site ${scopeId.slice(0, 8)}` : scopeId);
      const detail = reference.detail?.trim();
      const href =
        scopeType === 'site' && scopeId
          ? buildEvidenceScopeHref('site', scopeId)
          : undefined;
      return (
        <span key={`${scopeType}-${scopeId || index}`}>
          {index > 0 ? ', ' : null}
          {href ? (
            <a href={href} className="link-button">
              {label}
            </a>
          ) : (
            label
          )}
          {detail ? `: ${detail}` : null}
        </span>
      );
    });
  }
  return renderFindingDetailInlineText((evidence.values ?? []).join(', '), siteNameById);
};

const renderCoverageNoteDetail = (note: PostureCoverageNote): ReactNode => {
  if (note.items?.length) {
    return (
      <ul className="posture-detail-list">
        {note.items.map((item) => (
          <li key={item.key}>
            {item.href ? (
              <a href={item.href} className="link-button">
                {item.label}
              </a>
            ) : (
              item.label
            )}
            {item.detail ? `: ${item.detail}` : null}
          </li>
        ))}
      </ul>
    );
  }
  return note.detail ?? '';
};

const progressToken = (run?: PostureRunView | null, key?: string) => {
  if (!run || !key) {
    return '';
  }
  const value = run.progressSummary?.[key];
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
};

const progressCount = (run?: PostureRunView | null, key?: string) => {
  if (!run || !key) {
    return 0;
  }
  const value = run.progressSummary?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? value : 0;
  }
  if (typeof value === 'string') {
    return parsePositiveInt(value);
  }
  return 0;
};

const describeRunLifecycle = (run?: PostureRunView | null) => {
  if (!run) {
    return '';
  }
  const lifecycleStage = progressToken(run, 'lifecycle_stage');
  const executionState = progressToken(run, 'execution_state');
  const fanoutState = progressToken(run, 'fanout_state');
  const rollupState = progressToken(run, 'rollup_state');
  const sitesRunning = progressCount(run, 'sites_running');
  const sitesTerminal = progressCount(run, 'sites_terminal');
  const sitesStarted = progressCount(run, 'sites_started');

  if (run.scopeType === 'org') {
    if (lifecycleStage === 'terminal') {
      return '';
    }
    if (fanoutState === 'completed' && rollupState === 'waiting_for_sites') {
      return sitesRunning > 0
        ? `Fan-out complete, waiting on ${formatNumber(sitesRunning)} site run${sitesRunning === 1 ? '' : 's'}`
        : `Fan-out complete, waiting on ${formatNumber(Math.max(sitesStarted - sitesTerminal, 0))} site result${sitesStarted - sitesTerminal === 1 ? '' : 's'}`;
    }
    if (fanoutState === 'completed' && rollupState === 'ready') {
      return 'All site results received, final rollup ready';
    }
    if (fanoutState === 'queued') {
      return 'Fan-out has not started yet';
    }
  }

  if (executionState === 'queued') {
    return 'Queued for worker pickup';
  }
  if (executionState === 'cancel_requested' || lifecycleStage === 'cancelling') {
    return 'Cancellation requested, waiting for worker reconciliation';
  }
  if (executionState === 'running') {
    return 'Execution is in progress';
  }
  return '';
};

const summarizeRunLifecycleDetails = (run?: PostureRunView | null) => {
  if (!run) {
    return [];
  }
  const details: string[] = [];
  const lifecycleStage = progressToken(run, 'lifecycle_stage');
  const executionState = progressToken(run, 'execution_state');
  const fanoutState = progressToken(run, 'fanout_state');
  const rollupState = progressToken(run, 'rollup_state');
  const terminalStatus = progressToken(run, 'terminal_status');
  const sitesStarted = progressCount(run, 'sites_started');
  const sitesRunning = progressCount(run, 'sites_running');
  const sitesTerminal = progressCount(run, 'sites_terminal');
  const sitesFailed = progressCount(run, 'sites_failed');
  const sitesSkipped = progressCount(run, 'sites_skipped');
  const sitesCompletedWithGaps = progressCount(run, 'sites_completed_with_gaps');

  if (lifecycleStage) {
    details.push(`Lifecycle stage: ${humanizeToken(lifecycleStage)}.`);
  }
  if (executionState && run.scopeType !== 'org') {
    details.push(`Execution state: ${humanizeToken(executionState)}.`);
  }
  if (terminalStatus) {
    details.push(`Terminal outcome: ${humanizeToken(terminalStatus)}.`);
  }
  if (run.scopeType === 'org') {
    if (fanoutState) {
      details.push(`Fan-out state: ${humanizeToken(fanoutState)}.`);
    }
    if (rollupState) {
      details.push(`Rollup state: ${humanizeToken(rollupState)}.`);
    }
    if (sitesStarted > 0 || sitesRunning > 0 || sitesTerminal > 0 || sitesFailed > 0 || sitesSkipped > 0) {
      details.push(
        `Site run progress: ${formatNumber(sitesStarted)} started, ${formatNumber(sitesRunning)} running, ${formatNumber(sitesTerminal)} terminal, ${formatNumber(sitesFailed)} failed to start, ${formatNumber(sitesSkipped)} skipped as active duplicates.`
      );
    }
    if (sitesCompletedWithGaps > 0) {
      details.push(`${formatNumber(sitesCompletedWithGaps)} site run${sitesCompletedWithGaps === 1 ? '' : 's'} completed with gaps.`);
    }
  }
  return details;
};

const isForbiddenMessage = (value?: string | null) => {
  const token = normalizeToken(value);
  return token.includes('forbidden') || token.includes('status 403') || token.includes('permission');
};

const describePostureCollectionError = (resource: 'findings' | 'runs' | 'jobs', message: string) => {
  if (isForbiddenMessage(message)) {
    return `You do not have permission to view posture ${resource}. Ask an administrator to grant the required access for this organization.`;
  }
  return `Failed to load posture ${resource}: ${message}`;
};

const describePostureActionError = (resource: 'runs' | 'jobs', message: string) => {
  if (isForbiddenMessage(message)) {
    return `You do not have permission to modify posture ${resource}. Ask an administrator to grant the required access before retrying.`;
  }
  return message;
};

const UUID_LIKE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const buildDeviceInventoryHref = (deviceId: string) => `/app/inventory?deviceId=${encodeURIComponent(deviceId)}`;

const resolveScopeLabel = (
  scopeType: string,
  scopeId: string,
  siteNameById: Record<string, string>,
  orgDisplayName: string,
  deviceSummaryById: Record<string, PostureDeviceSummary | null> = {}
) => {
  if (scopeType === 'site') {
    return siteNameById[scopeId] ?? `Site ${scopeId.slice(0, 8)}`;
  }
  if (scopeType === 'org') {
    return orgDisplayName;
  }
  if (scopeType === 'device') {
    const device = deviceSummaryById[scopeId];
    return device?.hostname?.trim() || device?.displayName?.trim() || device?.mgmtAddress?.trim() || `Device ${scopeId.slice(0, 8)}`;
  }
  return `${humanizeToken(scopeType)} • ${scopeId || '—'}`;
};

const renderScopeLabel = (
  scopeType: string,
  scopeId: string,
  siteNameById: Record<string, string>,
  orgDisplayName: string,
  deviceSummaryById: Record<string, PostureDeviceSummary | null> = {}
) => {
  const label = resolveScopeLabel(scopeType, scopeId, siteNameById, orgDisplayName, deviceSummaryById);
  if (normalizeToken(scopeType) === 'device' && scopeId.trim()) {
    return (
      <a
        href={buildDeviceInventoryHref(scopeId.trim())}
        className="link-button"
        onClick={(event) => {
          event.stopPropagation();
        }}>
        {label}
      </a>
    );
  }
  return label;
};

interface EvidenceReferenceView {
  title: string;
  href?: string;
  referenceText?: string;
}

const resolveEvidenceReference = (
  record: PostureFindingRecord,
  evidence: PostureEvidence,
  siteNameById: Record<string, string>,
  orgDisplayName: string
): EvidenceReferenceView => {
  const locator = evidence.locator?.trim() ?? '';
  const category = normalizeToken(evidence.category);
  const kind = normalizeToken(evidence.kind);
  const metadata = evidence.metadata ?? {};

  if (kind === 'rule' || category === 'rule reference' || category === 'rule_reference') {
    return {
      title: humanizeRuleReference(locator),
      referenceText: locator || undefined
    };
  }

  const locatorMatch = locator.match(/^(org|site)\/([^/]+)\/([^/]+)$/i);
  const metadataScopeType = normalizeToken(metadata.scope_type);
  const metadataScopeId = (metadata.scope_id ?? '').trim();
  const scopeType = metadataScopeType === 'site' || metadataScopeType === 'org' ? metadataScopeType : locatorMatch?.[1]?.toLowerCase() ?? '';
  const scopeId = metadataScopeId || locatorMatch?.[2] || '';
  const summaryKey = locatorMatch?.[3] ?? '';

  if (scopeType === 'org') {
    return {
      title: describeEvidenceSummaryTitle('org', scopeId, summaryKey, siteNameById, orgDisplayName),
      href: buildEvidenceScopeHref('org', '', record.runId),
      referenceText: locator || undefined
    };
  }

  if (scopeType === 'site' && scopeId) {
    return {
      title: describeEvidenceSummaryTitle('site', scopeId, summaryKey, siteNameById, orgDisplayName),
      href: buildEvidenceScopeHref('site', scopeId),
      referenceText: locator || undefined
    };
  }

  return {
    title: locator || humanizeToken(kind || category || 'evidence'),
    referenceText: locator || undefined
  };
};

const buildEvidenceScopeHref = (scopeType: PostureScopeType, siteId: string, selectedRunId?: string) => {
  const routeState = readPostureRouteState();
  return buildPostureRouteHref('/app/posture', {
    ...routeState,
    scopeType,
    siteId: scopeType === 'site' ? siteId : '',
    selectedRunId: scopeType === 'org' ? selectedRunId ?? '' : ''
  });
};

const describeEvidenceSummaryTitle = (
  scopeType: 'org' | 'site',
  scopeId: string,
  summaryKey: string,
  siteNameById: Record<string, string>,
  orgDisplayName: string
) => {
  const scopeLabel = resolveScopeLabel(scopeType, scopeId, siteNameById, orgDisplayName);
  const normalizedKey = normalizeToken(summaryKey);
  switch (normalizedKey) {
    case 'fleet strengths':
    case 'fleet-strengths':
      return `${scopeLabel} fleet strengths summary`;
    case 'priority actions':
    case 'priority-actions':
      return `${scopeLabel} priority actions summary`;
    case 'strengths':
      return `${scopeLabel} strengths summary`;
    case 'multi domain weakness':
    case 'multi-domain-weakness':
      return `${scopeLabel} multi-domain weakness summary`;
    default:
      return `${scopeLabel} posture summary`;
  }
};

const humanizeRuleReference = (value: string) => {
  const token = value.trim();
  if (!token) {
    return 'Rule reference';
  }
  return token
    .replace(/[./]+/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const findingCategoryLabel = (finding: PostureFinding) => finding.bucketLabel?.trim() || humanizeToken(finding.domain);

const findingCategoryKey = (finding: PostureFinding) =>
  normalizeToken(finding.bucketId || finding.bucketLabel || finding.domain) || 'uncategorized';

const findingDisplayRuleLabel = (finding: PostureFinding) => finding.ruleLabel?.trim() || humanizeRuleReference(finding.ruleId);

const findingDisplayTitle = (finding: PostureFinding) => finding.plainTitle?.trim() || finding.title?.trim() || findingDisplayRuleLabel(finding);

const findingDisplayDetail = (finding: PostureFinding) => finding.plainDetail?.trim() || finding.detail?.trim() || findingDisplayRuleLabel(finding);

const findingDisplayAction = (finding: PostureFinding) => finding.plainAction?.trim() || finding.action?.trim() || '';

const resolveGeneratedRunName = (run: PostureRunView, siteNameById: Record<string, string>, orgDisplayName: string) => {
  const rawName = run.name?.trim() ?? '';
  if (!rawName) {
    return '';
  }
  if (run.scopeType === 'org') {
    if (!/^org-run(?:\s+.+)?$/i.test(rawName)) {
      return rawName;
    }
    const suffix = rawName.replace(/^org-run\s*/i, '').trim();
    if (!suffix || suffix === run.scopeId || UUID_LIKE_PATTERN.test(suffix)) {
      return 'org-run';
    }
  }
  if (run.scopeType === 'site') {
    if (!/^site-run(?:\s+.+)?$/i.test(rawName)) {
      return rawName;
    }
    const suffix = rawName.replace(/^site-run\s*/i, '').trim();
    if (!suffix || suffix === run.scopeId || UUID_LIKE_PATTERN.test(suffix)) {
      return `site-run ${resolveScopeLabel('site', run.scopeId, siteNameById, orgDisplayName)}`;
    }
  }
  return rawName;
};

const resolveRunDisplayName = (run: PostureRunView, siteNameById: Record<string, string>, orgDisplayName: string) =>
  resolveGeneratedRunName(run, siteNameById, orgDisplayName) || run.name?.trim() || shortRunId(run.runId);

const resolveRunLabel = (runId: string, runs: PostureRunView[], siteNameById: Record<string, string>, orgDisplayName: string) => {
  const run = runs.find((item) => item.runId === runId);
  return run ? resolveRunDisplayName(run, siteNameById, orgDisplayName) : `Run ${shortRunId(runId)}`;
};

const describeScopeSelector = (scopeSelector: Record<string, any>, siteNameById: Record<string, string>, orgDisplayName: string) => {
  const items: string[] = [];
  const siteId = typeof scopeSelector.siteId === 'string' ? scopeSelector.siteId.trim() : '';
  if (siteId) {
    items.push(`Site: ${resolveScopeLabel('site', siteId, siteNameById, orgDisplayName)}`);
  }

  const siteIds = Array.isArray(scopeSelector.siteIds)
    ? scopeSelector.siteIds.filter((value: unknown): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];
  if (siteIds.length > 0) {
    siteIds.forEach((value) => {
      items.push(`Site: ${resolveScopeLabel('site', value.trim(), siteNameById, orgDisplayName)}`);
    });
  }

  Object.entries(scopeSelector).forEach(([key, value]) => {
    if (key === 'siteId' || key === 'siteIds') {
      return;
    }
    if (Array.isArray(value)) {
      const normalized = value.map((item) => String(item)).filter(Boolean);
      if (normalized.length > 0) {
        items.push(`${humanizeToken(key)}: ${normalized.join(', ')}`);
      }
      return;
    }
    if (value != null && value !== '') {
      items.push(`${humanizeToken(key)}: ${String(value)}`);
    }
  });

  return items.length > 0 ? items : ['Stored scope selector'];
};

const resolveJobScopeLabel = (job: PostureJobView, siteNameById: Record<string, string>, orgDisplayName: string) => {
  if (job.scopeType === 'org') {
    return orgDisplayName;
  }
  const siteId = inferJobSiteId(job);
  return siteId ? siteNameById[siteId] ?? `Site ${siteId.slice(0, 8)}` : 'Scoped site set';
};

const resolveRunJobLabel = (jobId: string, jobs: PostureJobView[]) => {
  const job = jobs.find((item) => item.jobId === jobId);
  return job?.name?.trim() || `Job ${shortRunId(jobId)}`;
};

const inferJobSiteId = (job: PostureJobView) => {
  const direct = typeof job.scopeSelector?.siteId === 'string' ? job.scopeSelector.siteId.trim() : '';
  if (direct) {
    return direct;
  }
  const first = Array.isArray(job.scopeSelector?.siteIds) ? job.scopeSelector?.siteIds.find((value: unknown) => typeof value === 'string' && value.trim().length > 0) : '';
  return typeof first === 'string' ? first.trim() : '';
};

const formatSchedule = (schedule?: PostureJobSchedule) => {
  if (!schedule?.mode) {
    return '—';
  }
  if (schedule.mode === 'recurring') {
    return schedule.cron ? `Recurring • ${schedule.cron}` : 'Recurring';
  }
  return 'Manual';
};

const deltaStatusAccent = (
  status?: string | null,
  counts?: { new: number; persisting: number; cleared: number }
): 'default' | 'success' | 'warning' | 'danger' => {
  const normalized = normalizeToken(status);
  if (normalized === 'first_run') {
    return 'default';
  }
  if ((counts?.persisting ?? 0) > 0) {
    return 'danger';
  }
  if ((counts?.new ?? 0) > 0) {
    return 'warning';
  }
  if ((counts?.cleared ?? 0) > 0) {
    return 'success';
  }
  return normalized === 'compared' ? 'success' : 'default';
};

const deltaCountsAccent = (counts: { new: number; persisting: number; cleared: number }): 'default' | 'success' | 'warning' | 'danger' => {
  if (counts.persisting > 0) {
    return 'danger';
  }
  if (counts.new > 0) {
    return 'warning';
  }
  if (counts.cleared > 0) {
    return 'success';
  }
  return 'default';
};

const findingsByDomainLabel = (count?: number) => {
  if (!count) {
    return 'No active findings';
  }
  return `${formatNumber(count)} active findings`;
};

const scoreAccent = (score?: number): 'default' | 'success' | 'warning' | 'danger' => {
  if (typeof score !== 'number') {
    return 'default';
  }
  if (score >= 85) {
    return 'success';
  }
  if (score >= 65) {
    return 'warning';
  }
  return 'danger';
};

const findingsAccent = (rollup?: PostureFindingRollup): 'default' | 'success' | 'warning' | 'danger' => {
  if (!rollup) {
    return 'default';
  }
  const high = highSeverityCount(rollup.bySeverity);
  if (high > 0) {
    return 'danger';
  }
  if ((rollup.totalFindings ?? 0) > 0) {
    return 'warning';
  }
  return 'success';
};

const highSeverityCount = (bySeverity?: Record<string, number>) => (bySeverity?.high ?? 0) + (bySeverity?.critical ?? 0);

const summarizeSeverityCounts = (run?: PostureRunView | null, findingRecords?: PostureFindingRecord[]): Array<[string, number]> => {
  const counts = new Map<string, number>();
  if (findingRecords?.length) {
    findingRecords.forEach((record) => {
      const key = normalizeToken(record.finding.severity) || 'unknown';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
  } else if (run?.summary?.findingCounts) {
    Object.entries(run.summary.findingCounts).forEach(([severity, count]) => {
      if (count && count > 0) {
        counts.set(severity, count);
      }
    });
  }
  return Array.from(counts.entries()).sort((left, right) => compareSeverityDesc(left[0], right[0]) || right[1] - left[1]);
};

const summarizeDomainCounts = (findingRecords?: PostureFindingRecord[]): Array<[string, number]> => {
  const counts = new Map<string, { label: string; count: number }>();
  findingRecords?.forEach((record) => {
    const key = findingCategoryKey(record.finding);
    const current = counts.get(key);
    counts.set(key, {
      label: current?.label || findingCategoryLabel(record.finding),
      count: (current?.count ?? 0) + 1
    });
  });
  return Array.from(counts.values())
    .map((entry) => [entry.label, entry.count] as [string, number])
    .sort((left, right) => right[1] - left[1] || compareTextAsc(left[0], right[0]));
};

const buildRunFindingRecords = (run?: PostureRunView | null, childRuns?: PostureRunView[]): PostureFindingRecord[] => {
  if (!run) {
    return [];
  }
  if (run.findings?.length) {
    return run.findings.map((finding) => buildFindingRecordFromRun(run, finding));
  }
  if (run.scopeType !== 'org') {
    return [];
  }
  return buildChildRunFindingRecords(childRuns);
};

const buildChildRunFindingRecords = (childRuns?: PostureRunView[]): PostureFindingRecord[] => {
  const seen = new Set<string>();
  const items: PostureFindingRecord[] = [];
  childRuns?.forEach((childRun) => {
    childRun.findings?.forEach((finding) => {
      const record = buildFindingRecordFromRun(childRun, finding);
      const key = findingRowId(record);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      items.push(record);
    });
  });
  return items;
};

const findingIdentityKey = (finding: PostureFinding) =>
  JSON.stringify([finding.ruleId, finding.scopeType, finding.scopeId]);

const isHighPrioritySeverity = (severity?: string | null) => {
  const token = normalizeToken(severity);
  return token === 'critical' || token === 'high';
};

const severityToneClass = (severity?: string | null) => {
  switch (normalizeToken(severity)) {
    case 'critical':
    case 'high':
      return 'posture-chip--danger';
    case 'medium':
      return 'posture-chip--warning';
    case 'low':
    case 'info':
      return 'posture-chip--success';
    default:
      return 'posture-chip--neutral';
  }
};

const severityRank = (severity?: string | null) => {
  switch (normalizeToken(severity)) {
    case 'critical':
      return 4;
    case 'high':
      return 3;
    case 'medium':
      return 2;
    case 'low':
      return 1;
    case 'info':
      return 0;
    default:
      return -1;
  }
};

const compareSeverityDesc = (left?: string | null, right?: string | null) => severityRank(right) - severityRank(left);

const compareTextAsc = (left?: string | null, right?: string | null) => (left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base' });

const totalFindingCount = (findingCounts?: Record<string, number> | null) =>
  Object.values(findingCounts ?? {}).reduce((sum, count) => sum + (Number.isFinite(count) ? count : 0), 0);

const severityBadgeClass = (severity?: string | null) => {
  switch ((severity ?? '').trim().toLowerCase()) {
    case 'critical':
    case 'high':
      return 'ui-badge ui-badge--danger';
    case 'medium':
      return 'ui-badge ui-badge--warning';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

const coverageBadgeClass = (status?: string | null) => {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'complete':
      return 'ui-badge ui-badge--success';
    case 'degraded':
      return 'ui-badge ui-badge--warning';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

const statusBadgeClass = (status?: string | null) => {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'completed':
      return 'ui-badge ui-badge--success';
    case 'completed_with_gaps':
    case 'running':
    case 'cancel_requested':
      return 'ui-badge ui-badge--warning';
    case 'failed':
    case 'cancelled':
      return 'ui-badge ui-badge--danger';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

const jobStatusBadgeClass = (status?: string | null) => {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'active':
      return 'ui-badge ui-badge--success';
    case 'paused':
      return 'ui-badge ui-badge--warning';
    default:
      return 'ui-badge ui-badge--ghost';
  }
};

const isRunCancelable = (status?: string | null) => {
  const normalized = (status ?? '').trim().toLowerCase();
  return normalized === 'pending' || normalized === 'running' || normalized === 'cancel_requested';
};

export const readPostureSuccessResponse = async <T,>(
  response: Response,
  options?: PostureSuccessResponseOptions
): Promise<T | null> => {
  const requireJson = options?.requireJson ?? true;
  const contentType = response.headers.get('Content-Type') ?? '';
  const normalizedContentType = contentType.toLowerCase();
  if (normalizedContentType.includes('application/json')) {
    return (await response.json()) as T;
  }
  if (!requireJson && (response.status === 204 || response.status === 205 || normalizedContentType.length === 0)) {
    return null;
  }
  if (!requireJson) {
    return null;
  }
  throw new Error('Expected JSON response from posture API.');
};

const readResponseError = async (response: Response) => {
  try {
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.toLowerCase().includes('application/json')) {
      const payload = (await response.json()) as { error?: string; message?: string };
      return payload.error || payload.message || `Request failed with status ${response.status}`;
    }
    const text = await response.text();
    return text.trim() || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
};

export default PosturePage;

function FindingGroupMembers({row, resource, fetchPage, onSelect, scopeLabel}: {
  row: PostureFindingGroupRecord; resource: string;
  fetchPage: (resource: string) => Promise<PostureFindingList>;
  onSelect: (record: PostureFindingRecord) => void;
  scopeLabel: (record: PostureFindingRecord) => string;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PostureFindingList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true); setError(null);
    const [path, query] = resource.split('?');
    const params = new URLSearchParams(query);
    params.set('view', 'records'); params.set('ruleId', row.ruleId); params.set('domain', row.domain);
    params.set('limit', '25'); params.set('offset', String((page - 1) * 25));
    fetchPageRef.current(`${path}?${params}`).then(value => { if (active) setData(value); })
      .catch(err => { if (active) setError(String(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, page, resource, row.ruleId, row.domain]);
  return <details className="posture-group-row" onToggle={e => setOpen(e.currentTarget.open)}>
    <summary className="posture-group-row__summary"><div className="posture-table-cell">
      <span className="posture-table-cell__title">{row.title}</span>
      <span className="posture-table-cell__subtle">{row.detail} · {row.memberCount} findings across {row.scopeCount} scopes</span>
    </div></summary>
    <div className="posture-group-row__members">
      {loading ? <span>Loading findings…</span> : error ? <span role="alert">{error}</span> : data?.items.map((record,index) =>
        <button key={`${findingRowId(record)}-${index}`} type="button" className="posture-group-member" onClick={() => onSelect(record)}>
          <span className={severityBadgeClass(record.finding.severity)}>{humanizeToken(record.finding.severity)}</span>
          <span className="posture-group-member__scope">{scopeLabel(record)}</span>
          <span className="posture-group-member__detail">{findingDisplayDetail(record.finding)}</span>
        </button>)}
      <button type="button" disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
      <span>Page {page}</span>
      <button type="button" disabled={loading || !data || page * 25 >= data.total} onClick={() => setPage(p => p + 1)}>Next</button>
    </div>
  </details>;
}
